import {externalGuestDisplay} from '../../features/network/guestDisplay';
import {createRetryIdentity} from '../../features/network/requestIdentity';
import CoffeeFlatPricing from './CoffeeFlatPricing';
import ConsumptionStatistics from './ConsumptionStatistics';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useStationMode } from '../Common/useStationMode';
import { SuccessNotification, formatGermanDate, IndividualEmployeeProfile } from '../../App';
import { NewMenuItemModal, NewToppingItemModal } from './DepartmentAdminDashboard';
import MealOrderingStatusModal from './MealOrderingStatusModal';
import NightOnlyModeModal from './NightOnlyModeModal';
import MealSponsorModal from './MealSponsorModal';
import MealVariantAssignmentNotice from './MealVariantAssignmentNotice';
import OrderEditHistory from '../Common/OrderEditHistory';
import {
  applyNightOnlyHistoryUpdate,
  buildDailyMealPlanPayload,
} from './nightOnlyPlanning';
import { isNightOnlyDateEditable } from '../Employee/nightOnlyOrdering';
import { filterStatisticsEntries } from './statisticsBalanceFilter';
import { buildMealHistorySummary } from './mealHistorySummary';
import {
  buildEmployeeDisplaySections,
} from './employeeCategoryDisplay';
import PaymentDirectionSelector from './PaymentDirectionSelector';
import OrganizationFeatureSettings from './OrganizationFeatureSettings';
import {
  PAYMENT_DIRECTION_DEPOSIT,
  PAYMENT_DIRECTION_WITHDRAWAL,
  signedPaymentAmount,
} from './paymentDirection';
import { APP_VERSION } from '../../version';
import { Eye, EyeOff } from 'lucide-react';
import { getBerlinDate } from '../../utils/berlinDate';
import ButterPreparationNote, {butterPreparationLabel} from '../Common/ButterPreparationNote';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export const BreakfastSummaryTable = ({ departmentId, onClose }) => {
  const [dailySummary, setDailySummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [toppingsMenu, setToppingsMenu] = useState([]);
  const [showMealStatusModal, setShowMealStatusModal] = useState(false);
  const [showNightOnlyModal, setShowNightOnlyModal] = useState(false);
  const [nightOnly, setNightOnly] = useState(false);
  const [eggNames, setEggNames] = useState({
    boiled: 'Gekochte Eier',
    fried: 'Spiegeleier',
    scrambled: 'Rührei'
  });

  useEffect(() => {
    fetchDailySummary();
    fetchToppingsMenu();
    fetchEggNames();
    fetchNightOnlyStatus();
  }, [departmentId]);

  const fetchNightOnlyStatus = async () => {
    if (!departmentId) return;
    try {
      const response = await axios.get(`${API}/daily-night-only/${departmentId}/${getBerlinDate()}`);
      setNightOnly(Boolean(response.data?.night_only));
    } catch (error) {
      console.error('Nachtdienst-Modus konnte nicht geladen werden:', error);
      setNightOnly(false);
    }
  };

  const fetchToppingsMenu = async () => {
    try {
      if (!departmentId) {
        console.error('No department ID available for toppings fetch');
        return;
      }
      
      const response = await axios.get(`${API}/menu/toppings/${departmentId}`);
      setToppingsMenu(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Beläge:', error);
      // Fallback to old endpoint
      try {
        const response = await axios.get(`${API}/menu/toppings`);
        setToppingsMenu(response.data);
      } catch (fallbackError) {
        console.error('Fallback toppings loading failed:', fallbackError);
      }
    }
  };

  const fetchEggNames = async () => {
    try {
      if (!departmentId) return;
      const response = await axios.get(`${API}/department-settings/${departmentId}`);
      setEggNames({
        boiled: response.data.boiled_eggs_name || 'Gekochte Eier',
        fried: response.data.fried_eggs_name || 'Spiegeleier',
        scrambled: response.data.scrambled_eggs_name || 'Rührei'
      });
    } catch (error) {
      console.error('Fehler beim Laden der Eiernamen:', error);
    }
  };

  const fetchDailySummary = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/orders/daily-summary/${departmentId}`);
      setDailySummary(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Tagesübersicht:', error);
      alert('Fehler beim Laden der Übersicht');
    } finally {
      setIsLoading(false);
    }
  };

  const rollTypeLabels = {
    'weiss': 'Helles Brötchen',
    'koerner': 'Körnerbrötchen'
  };

  // Use dynamic labels from menu if available, otherwise fall back to defaults
  const defaultLabels = {
    'ruehrei': 'Rührei',
    'spiegelei': 'Spiegelei',
    'eiersalat': 'Eiersalat',
    'salami': 'Salami',
    'schinken': 'Schinken',
    'kaese': 'Käse',
    'butter': 'Butter'
  };
  
  const toppingLabels = {};
  toppingsMenu.forEach(item => {
    // Ensure we always get a string value
    const customName = item.name;
    const fallbackName = defaultLabels[item.topping_type];
    const finalName = customName || fallbackName || item.topping_type;
    
    // Make sure the final value is a string and not an object
    toppingLabels[item.topping_type] = typeof finalName === 'string' ? finalName : String(finalName);
  });

  // Fallback toppings if menu is empty or missing items
  const finalToppingLabels = { ...defaultLabels, ...toppingLabels };

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Lade Essensübersicht...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-50 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-gradient-to-b from-gray-50 to-white rounded-2xl max-w-6xl w-full mx-2 sm:mx-4 my-4 sm:my-8 overflow-y-auto shadow-2xl scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', maxHeight: 'calc(100dvh - 2rem)', maxHeight: 'calc(95vh - 2rem)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modern Header */}
        <div className="sticky top-0 z-20 bg-gradient-to-r from-blue-600 to-blue-700 p-4 sm:p-6 rounded-t-2xl">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg sm:text-2xl font-bold text-white">Essensübersicht (Tagesansicht)</h2>
              <p className="text-blue-100 text-sm mt-1">{formatGermanDate(dailySummary?.date)}</p>
            </div>
            <div className="flex items-center justify-between sm:justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowNightOnlyModal(true)}
                className={`min-h-10 px-3 py-2 rounded-lg border text-sm font-semibold text-white transition-colors ${
                  nightOnly
                    ? 'border-amber-200/70 bg-amber-500/80 hover:bg-amber-500'
                    : 'border-white/30 bg-white/15 hover:bg-white/25'
                }`}
                data-testid="employee-night-only-action"
              >
                {nightOnly ? 'Nachtdienst aktiv' : 'Heute Nachtdienst'}
              </button>
              <button
                type="button"
                onClick={() => setShowMealStatusModal(true)}
                className="min-h-10 px-3 py-2 rounded-lg border border-white/30 bg-white/15 text-sm font-semibold text-white transition-colors hover:bg-white/25"
                data-testid="employee-breakfast-status-action"
              >
                Bestellungen sperren
              </button>
              <button
                onClick={onClose}
                className="w-10 h-10 shrink-0 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white text-xl transition-all"
                aria-label="Essensübersicht schließen"
              >
                ×
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 pb-16 sm:pb-20">
          {dailySummary && dailySummary.shopping_list && Object.keys(dailySummary.shopping_list).length > 0 ? (
            <div className="space-y-6">
              {/* Combined Shopping List with Lunch Box */}
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Main Shopping List Box */}
                <div className="flex-1 bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-2xl p-4 sm:p-6 shadow-sm">
                  <h3 className="text-lg sm:text-xl font-bold mb-4 text-emerald-800 flex items-center gap-2">
                    <span className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center text-white text-sm">✓</span>
                    Einkaufsliste
                  </h3>
                  
                  <div className="bg-white border border-emerald-200 rounded-xl p-4 shadow-inner">
                    <div className="space-y-4">
                      {/* Bread Rolls */}
                      <div className="pb-3 border-b border-gray-100">
                        <div className="font-semibold text-gray-700 mb-2 text-sm uppercase tracking-wide">Brötchen gesamt</div>
                        <div className="ml-2 space-y-1">
                          {Object.entries(dailySummary.shopping_list).map(([rollType, data]) => {
                            const rollLabel = rollTypeLabels[rollType] || rollType;
                            const wholeRolls = data.whole_rolls || 0;
                            const halves = data.halves || 0;
                            const leftover = (wholeRolls * 2) - halves;
                            return (
                              <div key={rollType} className="flex items-center gap-2">
                                <span className="text-2xl font-bold text-emerald-600">{wholeRolls}</span>
                                <span className="text-gray-600">{rollLabel.replace(' Brötchen', '')}</span>
                                {leftover > 0 ? (
                                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full" data-testid={`leftover-${rollType}`}>
                                    {leftover} Hälfte übrig
                                  </span>
                                ) : (
                                  <span className="text-xs text-emerald-500" data-testid={`leftover-${rollType}`}>
                                    geht auf
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Eggs Section */}
                      {(dailySummary.total_boiled_eggs > 0 || dailySummary.total_fried_eggs > 0 || dailySummary.total_scrambled_eggs > 0) && (
                        <div className="pb-3 border-b border-gray-100">
                          <div className="font-semibold text-gray-700 mb-2 text-sm uppercase tracking-wide">Eier</div>
                          <div className="ml-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
                            {dailySummary.total_boiled_eggs > 0 && (
                              <div className="flex items-center gap-2 bg-yellow-50 px-3 py-2 rounded-lg">
                                <span className="text-xl font-bold text-yellow-600">{dailySummary.total_boiled_eggs}</span>
                                <span className="text-gray-600 text-sm">{eggNames.boiled}</span>
                              </div>
                            )}
                            {dailySummary.total_fried_eggs > 0 && (
                              <div className="flex items-center gap-2 bg-orange-50 px-3 py-2 rounded-lg">
                                <span className="text-xl font-bold text-orange-600">{dailySummary.total_fried_eggs}</span>
                                <span className="text-gray-600 text-sm">{eggNames.fried}</span>
                              </div>
                            )}
                            {dailySummary.total_scrambled_eggs > 0 && (
                              <div className="flex items-center gap-2 bg-amber-50 px-3 py-2 rounded-lg">
                                <span className="text-xl font-bold text-amber-600">{dailySummary.total_scrambled_eggs}</span>
                                <span className="text-gray-600 text-sm">{eggNames.scrambled}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Toppings with Roll Type Detail */}
                      {(() => {
                        // Check if we have employee orders data
                        if (!dailySummary.employee_orders || Object.keys(dailySummary.employee_orders).length === 0) {
                          return null;
                        }
                        
                        // Calculate toppings from employee orders
                        const toppingBreakdown = {};
                        
                        try {
                          Object.values(dailySummary.employee_orders).forEach(employeeData => {
                            if (employeeData && employeeData.toppings) {
                              Object.entries(employeeData.toppings).forEach(([topping, count]) => {
                                if (!toppingBreakdown[topping]) {
                                  toppingBreakdown[topping] = { white: 0, seeded: 0, without_butter_white: 0, without_butter_seeded: 0 };
                                }
                                
                                // Check if the count is already broken down by roll type (new format)
                                if (typeof count === 'object' && count.white !== undefined && count.seeded !== undefined) {
                                  // New format: direct assignment from backend
                                  toppingBreakdown[topping].white += count.white || 0;
                                  toppingBreakdown[topping].seeded += count.seeded || 0;
                                  toppingBreakdown[topping].without_butter_white += count.without_butter_white || 0;
                                  toppingBreakdown[topping].without_butter_seeded += count.without_butter_seeded || 0;
                                } else {
                                  // Legacy format: proportional distribution (fallback)
                                  const whiteHalves = employeeData.white_halves || 0;
                                  const seededHalves = employeeData.seeded_halves || 0;
                                  const totalHalves = whiteHalves + seededHalves;
                                  
                                  if (totalHalves > 0) {
                                    const whiteRatio = whiteHalves / totalHalves;
                                    const seededRatio = seededHalves / totalHalves;
                                    toppingBreakdown[topping].white += Math.round(count * whiteRatio);
                                    toppingBreakdown[topping].seeded += Math.round(count * seededRatio);
                                  }
                                }
                              });
                            }
                          });
                        } catch (error) {
                          console.error('Error processing toppings:', error);
                          return (
                            <div>
                              <div className="font-semibold text-gray-700 mb-2 text-sm uppercase tracking-wide">Beläge</div>
                              <div className="ml-2 text-red-500 text-sm">Fehler beim Laden der Beläge</div>
                            </div>
                          );
                        }
                        
                        if (Object.keys(toppingBreakdown).length === 0) {
                          return null;
                        }
                        
                        const toppingItems = [];
                        Object.entries(toppingBreakdown).forEach(([topping, counts]) => {
                          const toppingName = finalToppingLabels[topping] || topping;
                          
                          // Create parts array for Hell and Korn counts
                          const parts = [];
                          if (counts.white > 0) {
                            parts.push(`${counts.white}x Hell`);
                          }
                          if (counts.seeded > 0) {
                            parts.push(`${counts.seeded}x Korn`);
                          }
                          
                          // Only add if there are actual counts
                          if (parts.length > 0) {
                            toppingItems.push(
                              <div key={topping} className="py-1 text-sm">
                                <div className="flex items-start gap-3">
                                  <span className="text-gray-700 w-24 shrink-0 sm:w-32">{toppingName}</span>
                                  <span className="text-gray-600 font-medium">{parts.join(' / ')}</span>
                                </div>
                                <ButterPreparationNote counts={counts} />
                              </div>
                            );
                          }
                        });
                        
                        if (toppingItems.length === 0) {
                          return null;
                        }
                        
                        return (
                          <div className="pb-3 border-b border-gray-100">
                            <div className="font-semibold text-gray-700 mb-2 text-sm uppercase tracking-wide">Beläge</div>
                            <div className="ml-2 bg-gray-50 rounded-lg p-3 space-y-1">
                              {toppingItems}
                            </div>
                          </div>
                        );
                      })()}
                      
                      {/* Extras & Sonderwünsche Section - within shopping list */}
                      {(() => {
                        // Use the new notes_summary from backend if available  
                        const notesEntries = dailySummary?.notes_summary ? 
                          Object.entries(dailySummary.notes_summary) : [];

                        if (notesEntries.length === 0) {
                          return null;
                        }

                        return (
                          <div>
                            <div className="font-semibold text-gray-700 mb-2 text-sm uppercase tracking-wide">Extras & Sonderwünsche</div>
                            <div className="ml-2 space-y-2">
                              {notesEntries.map(([employeeName, notes], index) => (
                                <div key={index} className="text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                  <span className="font-semibold text-amber-800">{employeeName}:</span>
                                  <span className="text-gray-700 ml-1">{notes}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                      
                      {/* Purchase List (Einkaufsliste für Toppings mit Grammangabe) */}
                      {dailySummary.purchase_list && dailySummary.purchase_list.length > 0 && (
                        <div data-testid="purchase-list-section">
                          <div className="font-semibold text-gray-700 mb-2 text-sm uppercase tracking-wide">Einkaufsmengen</div>
                          <div className="ml-2 space-y-2">
                            {dailySummary.purchase_list.map((item, index) => (
                              <div key={index} className="flex items-center justify-between bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                                <div>
                                  <span className="font-semibold text-rose-800">{item.topping_name}</span>
                                  <span className="text-xs text-rose-500 ml-2">({item.grams_per_half}g x {item.total_halves} Hälften)</span>
                                </div>
                                <span className="text-lg font-bold text-rose-700" data-testid={`purchase-grams-${index}`}>
                                  {item.total_grams >= 1000 
                                    ? `${(item.total_grams / 1000).toFixed(1).replace('.', ',')} kg` 
                                    : `${item.total_grams} g`}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Lunch Summary Box */}
                {(() => {
                  const lunchPortions = dailySummary.employee_orders ? 
                    Object.values(dailySummary.employee_orders).reduce((sum, emp) => sum + (emp.has_lunch ? (emp.lunch_count || 1) : 0), 0) : 0;
                  
                  if (lunchPortions > 0) {
                    const lunchVars = dailySummary.lunch_variants_summary || {};
                    const lunchVarEntries = Object.entries(lunchVars).filter(([, c]) => (c || 0) > 0);
                    return (
                      <div className="lg:w-52 bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-2xl p-5 shadow-sm">
                        <h4 className="text-lg font-bold text-violet-800 mb-4 flex items-center gap-2">
                          <span className="w-8 h-8 bg-violet-500 rounded-lg flex items-center justify-center text-white text-sm">M</span>
                          Mittagessen
                        </h4>
                        <div className="text-center bg-white rounded-xl p-4 shadow-inner">
                          <div className="text-4xl font-bold text-violet-600 mb-1" data-testid="lunch-total-portions">{lunchPortions}</div>
                          <div className="text-sm text-violet-500 font-medium">{lunchPortions === 1 ? 'Portion' : 'Portionen'}</div>
                          {lunchVarEntries.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-violet-100 space-y-1 text-left" data-testid="lunch-variants-breakdown">
                              {lunchVarEntries.map(([name, count]) => (
                                <div key={name} className="flex items-start justify-between gap-2 text-xs">
                                  <span className="min-w-0 flex-1 whitespace-normal break-words text-violet-700">{name}</span>
                                  <span className="shrink-0 font-semibold text-violet-800 whitespace-nowrap">× {count}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Dinner Summary Box */}
                {(() => {
                  const dinnerPortions = dailySummary.employee_orders ? 
                    Object.values(dailySummary.employee_orders).reduce((sum, emp) => sum + (emp.has_dinner ? (emp.dinner_count || 1) : 0), 0) : 0;
                  if (dinnerPortions > 0) {
                    const dinnerVars = dailySummary.dinner_variants_summary || {};
                    const dinnerVarEntries = Object.entries(dinnerVars).filter(([, c]) => (c || 0) > 0);
                    return (
                      <div className="lg:w-52 bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl p-5 shadow-sm">
                        <h4 className="text-lg font-bold text-indigo-800 mb-4 flex items-center gap-2">
                          <span className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center text-white text-sm">A</span>
                          Abendessen
                        </h4>
                        <div className="text-center bg-white rounded-xl p-4 shadow-inner">
                          <div className="text-4xl font-bold text-indigo-600 mb-1" data-testid="dinner-total-portions">{dinnerPortions}</div>
                          <div className="text-sm text-indigo-500 font-medium">{dinnerPortions === 1 ? 'Portion' : 'Portionen'}</div>
                          {dinnerVarEntries.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-indigo-100 space-y-1 text-left" data-testid="dinner-variants-breakdown">
                              {dinnerVarEntries.map(([name, count]) => (
                                <div key={name} className="flex items-start justify-between gap-2 text-xs">
                                  <span className="min-w-0 flex-1 whitespace-normal break-words text-indigo-700">{name}</span>
                                  <span className="shrink-0 font-semibold text-indigo-800 whitespace-nowrap">× {count}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* Matrix-Style Employee Orders Table */}
              <div>
                <h3 className="text-lg font-bold mb-4 text-gray-800 flex items-center gap-2">
                  <span className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center text-white text-sm">☰</span>
                  Detaillierte Mitarbeiter-Bestellungen
                </h3>
                
                {dailySummary && dailySummary.employee_orders && Object.keys(dailySummary.employee_orders).length > 0 ? (
                  (() => {
                    try {
                      // Filter employees who have any meal bookings (breakfast/lunch/dinner or sponsored)
                      const employeesWithBookings = Object.entries(dailySummary.employee_orders).filter(([employeeName, employeeData]) => {
                        if (!employeeData) return false;
                        const hasRolls = (employeeData.white_halves || 0) > 0 || (employeeData.seeded_halves || 0) > 0;
                        const hasEggs = (employeeData.boiled_eggs || 0) > 0 || (employeeData.fried_eggs || 0) > 0 || (employeeData.scrambled_eggs || 0) > 0;
                        const hasToppings = employeeData.toppings && Object.keys(employeeData.toppings).length > 0;
                        const hasLunch = employeeData.has_lunch;
                        const hasDinner = employeeData.has_dinner;
                        const isSponsored = employeeData.is_sponsored;
                        // Include sponsored employees even if their individual counts are 0
                        return hasRolls || hasEggs || hasToppings || hasLunch || hasDinner || isSponsored;
                      });
                      
                      if (employeesWithBookings.length === 0) {
                        return (
                          <div className="text-center py-4 text-gray-500">
                            Keine Mitarbeiter-Bestellungen für heute
                          </div>
                        );
                      }
                      
                      // Get all unique toppings
                      const allToppings = new Set();
                      employeesWithBookings.forEach(([employeeName, employeeData]) => {
                        if (employeeData && employeeData.toppings) {
                          Object.keys(employeeData.toppings).forEach(topping => {
                            const count = employeeData.toppings[topping];
                            const quantity = typeof count === 'object' && count !== null
                              ? Number(count.white || 0) + Number(count.seeded || 0) : Number(count || 0);
                            if (quantity > 0) allToppings.add(topping);
                          });
                        }
                      });
                      const toppingsList = Array.from(allToppings);
                      
                      // Count lunch portions (Phase 2: employee may order multiple)
                      const totalLunchCount = employeesWithBookings.reduce(
                        (sum, [name, data]) => sum + (data && data.has_lunch ? (data.lunch_count || 1) : 0),
                        0
                      );
                      const totalDinnerCount = employeesWithBookings.reduce(
                        (sum, [name, data]) => sum + (data && data.has_dinner ? (data.dinner_count || 1) : 0),
                        0
                      );
                      
                      const hasBoiledEggs = employeesWithBookings.some(([, data]) => Number(data.boiled_eggs) > 0);
                      const hasFriedEggs = employeesWithBookings.some(([, data]) => Number(data.fried_eggs) > 0);
                      const hasScrambledEggs = employeesWithBookings.some(([, data]) => Number(data.scrambled_eggs) > 0);

                      return (
                        <div className="overflow-x-auto -mx-4 sm:mx-0">
                          <div className="inline-block min-w-full align-middle">
                            <div className="overflow-hidden rounded-xl shadow-sm border border-gray-200">
                              <table className="min-w-full border-collapse">
                                <thead>
                                  <tr className="bg-gray-100">
                                    <th className="sticky left-0 z-10 px-2 sm:px-3 py-3 text-left font-semibold text-xs sm:text-sm bg-gray-100 min-w-[100px] sm:w-32 border-r border-gray-200">
                                      Mitarbeiter
                                    </th>
                                    {totalLunchCount > 0 && (<th className="px-1 py-3 text-center font-semibold text-[10px] sm:text-xs bg-violet-50 min-w-[45px] sm:w-20 border-r border-gray-200">
                                      Mittag
                                    </th>)}
                                    {totalDinnerCount > 0 && (<th className="px-1 py-3 text-center font-semibold text-[10px] sm:text-xs bg-indigo-50 min-w-[45px] sm:w-20 border-r border-gray-200">
                                      Abend
                                    </th>)}
                                    {toppingsList.map(topping => (
                                      <th key={topping} className="px-1 py-3 text-center font-semibold text-[10px] sm:text-xs min-w-[55px] sm:w-20 border-r border-gray-200 last:border-r-0">
                                        <div className="break-words leading-tight">{String(finalToppingLabels[topping] || topping)}</div>
                                      </th>
                                    ))}
                                    {hasBoiledEggs && (<th className="px-1 py-3 text-center font-semibold text-[10px] sm:text-xs bg-yellow-50 w-12 sm:w-14 border-r border-gray-200">
                                      <div className="break-words leading-tight text-[9px] sm:text-xs">{eggNames.boiled}</div>
                                    </th>)}
                                    {hasFriedEggs && (<th className="px-1 py-3 text-center font-semibold text-[10px] sm:text-xs bg-yellow-50 w-12 sm:w-14 border-r border-gray-200">
                                      <div className="break-words leading-tight text-[9px] sm:text-xs">{eggNames.fried}</div>
                                    </th>)}
                                    {hasScrambledEggs && (<th className="px-1 py-3 text-center font-semibold text-[10px] sm:text-xs bg-yellow-50 w-12 sm:w-14">
                                      <div className="break-words leading-tight text-[9px] sm:text-xs">{eggNames.scrambled}</div>
                                    </th>)}
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                  {employeesWithBookings.map(([employeeName, employeeData], index) => (
                                    <tr key={employeeName} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                                      <td className={`sticky left-0 z-10 px-2 py-2 font-semibold text-[11px] sm:text-sm truncate border-r border-gray-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} title={String(employeeName)}>
                                        {String(employeeName)}
                                      </td>
                                      {totalLunchCount > 0 && (<td className="px-1 py-2 text-center text-[11px] sm:text-sm bg-violet-50/50 border-r border-gray-200">
                                        {(employeeData && employeeData.has_lunch) ? (
                                          <div className="flex flex-col items-center gap-0.5">
                                            <span className="text-violet-600 font-bold">
                                              {(employeeData.lunch_count || 1) > 1 ? `×${employeeData.lunch_count}` : '✓'}
                                            </span>
                                            {(() => {
                                              const vars = employeeData.lunch_variants || {};
                                              const entries = Object.entries(vars).filter(([, c]) => (c || 0) > 0);
                                              if (entries.length === 0) return null;
                                              // Skip breakdown only when there's exactly one generic default variant (no real variant name)
                                              if (entries.length === 1 && entries[0][0] === 'Mittagessen') return null;
                                              return (
                                                <div className="text-[9px] leading-tight text-violet-500 space-y-0.5">
                                                  {entries.map(([name, count]) => (
                                                    <div key={name}>{count}× {name}</div>
                                                  ))}
                                                </div>
                                              );
                                            })()}
                                          </div>
                                        ) : (
                                          <span className="text-gray-300">-</span>
                                        )}
                                      </td>)}
                                      {totalDinnerCount > 0 && (<td className="px-1 py-2 text-center text-[11px] sm:text-sm bg-indigo-50/50 border-r border-gray-200">
                                        {(employeeData && employeeData.has_dinner) ? (
                                          <div className="flex flex-col items-center gap-0.5">
                                            <span className="text-indigo-600 font-bold">
                                              {(employeeData.dinner_count || 1) > 1 ? `×${employeeData.dinner_count}` : '✓'}
                                            </span>
                                            {(() => {
                                              const vars = employeeData.dinner_variants || {};
                                              const entries = Object.entries(vars).filter(([, c]) => (c || 0) > 0);
                                              if (entries.length === 0) return null;
                                              if (entries.length === 1 && entries[0][0] === 'Abendessen') return null;
                                              return (
                                                <div className="text-[9px] leading-tight text-indigo-500 space-y-0.5">
                                                  {entries.map(([name, count]) => (
                                                    <div key={name}>{count}× {name}</div>
                                                  ))}
                                                </div>
                                              );
                                            })()}
                                          </div>
                                        ) : (
                                          <span className="text-gray-300">-</span>
                                        )}
                                      </td>)}
                                  {toppingsList.map(topping => {
                                    const toppingCount = (employeeData && employeeData.toppings && employeeData.toppings[topping]) || 0;
                                    
                                    if (toppingCount === 0 || (typeof toppingCount === 'object' && (toppingCount.white || 0) + (toppingCount.seeded || 0) === 0)) {
                                      return (
                                        <td key={topping} className="px-1 py-2 text-center text-[10px] sm:text-xs text-gray-300 border-r border-gray-100">
                                          -
                                        </td>
                                      );
                                    }
                                    
                                    let whiteCount = 0;
                                    let seededCount = 0;
                                    let totalCount = 0;
                                    
                                    // Check if the count is already broken down by roll type (new format)
                                    if (typeof toppingCount === 'object' && toppingCount.white !== undefined && toppingCount.seeded !== undefined) {
                                      // New format: use direct assignment from backend
                                      whiteCount = toppingCount.white || 0;
                                      seededCount = toppingCount.seeded || 0;
                                      totalCount = whiteCount + seededCount;
                                    } else {
                                      // Legacy format: proportional distribution (fallback)
                                      const whiteHalves = (employeeData && employeeData.white_halves) || 0;
                                      const seededHalves = (employeeData && employeeData.seeded_halves) || 0;
                                      const totalHalves = whiteHalves + seededHalves;
                                      totalCount = toppingCount;
                                      
                                      if (totalHalves === 0) {
                                        return (
                                          <td key={topping} className="px-1 py-2 text-center text-[10px] sm:text-xs font-medium border-r border-gray-100">
                                            {totalCount}
                                          </td>
                                        );
                                      }
                                      
                                      // Calculate proportional distribution
                                      const whiteRatio = whiteHalves / totalHalves;
                                      const seededRatio = seededHalves / totalHalves;
                                      whiteCount = Math.round(totalCount * whiteRatio);
                                      seededCount = Math.round(totalCount * seededRatio);
                                    }
                                    
                                    // Format with abbreviations  
                                    const parts = [];
                                    if (seededCount > 0) parts.push(`${seededCount}K`);
                                    if (whiteCount > 0) parts.push(`${whiteCount}N`);
                                    
                                    return (
                                      <td key={topping} className="px-1 py-2 text-center text-[10px] sm:text-xs font-medium text-gray-700 border-r border-gray-100">
                                        {parts.join('/') || totalCount.toString()}
                                        <ButterPreparationNote counts={toppingCount} compact />
                                      </td>
                                    );
                                  })}
                                  {hasBoiledEggs && (<td className="px-1 py-2 text-center text-[10px] sm:text-xs bg-yellow-50/50 font-medium border-r border-gray-100">
                                    {(employeeData && employeeData.boiled_eggs) || <span className="text-gray-300">-</span>}
                                  </td>)}
                                  {hasFriedEggs && (<td className="px-1 py-2 text-center text-[10px] sm:text-xs bg-yellow-50/50 font-medium border-r border-gray-100">
                                    {(employeeData && employeeData.fried_eggs) || <span className="text-gray-300">-</span>}
                                  </td>)}
                                  {hasScrambledEggs && (<td className="px-1 py-2 text-center text-[10px] sm:text-xs bg-yellow-50/50 font-medium">
                                    {(employeeData && employeeData.scrambled_eggs) || <span className="text-gray-300">-</span>}
                                  </td>)}
                                </tr>
                              ))}
                              
                                  {/* Totals Row */}
                                  <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                                    <td className="sticky left-0 z-10 px-2 py-3 text-[11px] sm:text-sm bg-gray-100 border-r border-gray-200">
                                      Gesamt
                                    </td>
                                    {totalLunchCount > 0 && (<td className="px-1 py-3 text-center text-[10px] sm:text-xs bg-violet-100 font-bold border-r border-gray-200">
                                      <div>{totalLunchCount}</div>
                                      {(() => {
                                        const vars = dailySummary.lunch_variants_summary || {};
                                        const entries = Object.entries(vars).filter(([, c]) => (c || 0) > 0);
                                        if (entries.length <= 1) return null;
                                        return (
                                          <div className="mt-1 text-[9px] leading-tight font-normal text-violet-700 space-y-0.5">
                                            {entries.map(([name, count]) => (
                                              <div key={name}>{count}× {name}</div>
                                            ))}
                                          </div>
                                        );
                                      })()}
                                    </td>)}
                                    {totalDinnerCount > 0 && (<td className="px-1 py-3 text-center text-[10px] sm:text-xs bg-indigo-100 font-bold border-r border-gray-200">
                                      <div>{totalDinnerCount}</div>
                                      {(() => {
                                        const vars = dailySummary.dinner_variants_summary || {};
                                        const entries = Object.entries(vars).filter(([, c]) => (c || 0) > 0);
                                        if (entries.length <= 1) return null;
                                        return (
                                          <div className="mt-1 text-[9px] leading-tight font-normal text-indigo-700 space-y-0.5">
                                            {entries.map(([name, count]) => (
                                              <div key={name}>{count}× {name}</div>
                                            ))}
                                          </div>
                                        );
                                      })()}
                                    </td>)}
                                {toppingsList.map(topping => {
                                  let totalWhite = 0;
                                  let totalSeeded = 0;
                                  const withoutButter = {without_butter_white: 0, without_butter_seeded: 0};
                                  
                                  employeesWithBookings.forEach(([name, employeeData]) => {
                                    if (employeeData && employeeData.toppings && employeeData.toppings[topping]) {
                                      const count = employeeData.toppings[topping];
                                      
                                      // FIXED: Use new backend format {white: count, seeded: count}
                                      if (typeof count === 'object' && count.white !== undefined && count.seeded !== undefined) {
                                        // New format: direct assignment from backend
                                        totalWhite += count.white || 0;
                                        totalSeeded += count.seeded || 0;
                                        withoutButter.without_butter_white += count.without_butter_white || 0;
                                        withoutButter.without_butter_seeded += count.without_butter_seeded || 0;
                                      } else {
                                        // Legacy format: proportional distribution (fallback)
                                        const whiteHalves = employeeData.white_halves || 0;
                                        const seededHalves = employeeData.seeded_halves || 0;
                                        const totalHalves = whiteHalves + seededHalves;
                                        
                                        if (totalHalves > 0) {
                                          totalWhite += Math.round(count * (whiteHalves / totalHalves));
                                          totalSeeded += Math.round(count * (seededHalves / totalHalves));
                                        }
                                      }
                                    }
                                  });
                                  
                                  const parts = [];
                                  if (totalSeeded > 0) parts.push(`${totalSeeded}K`);
                                  if (totalWhite > 0) parts.push(`${totalWhite}N`);
                                  
                                  return (
                                    <td key={topping} className="px-1 py-3 text-center text-[10px] sm:text-xs font-bold border-r border-gray-200">
                                      {parts.join('/') || '-'}
                                      <ButterPreparationNote counts={withoutButter} compact />
                                    </td>
                                  );
                                })}
                                {hasBoiledEggs && (<td className="px-1 py-3 text-center text-[10px] sm:text-xs bg-yellow-100 font-bold border-r border-gray-200">
                                  {dailySummary.total_boiled_eggs || 0}
                                </td>)}
                                {hasFriedEggs && (<td className="px-1 py-3 text-center text-[10px] sm:text-xs bg-yellow-100 font-bold border-r border-gray-200">
                                  {dailySummary.total_fried_eggs || 0}
                                </td>)}
                                {hasScrambledEggs && (<td className="px-1 py-3 text-center text-[10px] sm:text-xs bg-yellow-100 font-bold">
                                  {dailySummary.total_scrambled_eggs || 0}
                                </td>)}
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>

                          
                          {/* Mobile Scroll Hint */}
                          <div className="mt-3 text-xs text-gray-500 bg-gray-50 p-2 rounded-lg sm:hidden">
                            <strong>Tipp:</strong> Wischen um alle Spalten zu sehen
                          </div>
                          
                          {/* Legend */}
                          <div className="mt-4 text-xs text-gray-500 bg-gray-50 p-2 rounded-lg">
                            <strong>Legende:</strong> K = Körner, N = Hell (z.B. "2K/1N" = 2x Körner + 1x Hell)
                          </div>
                          {/* Spacer for iOS Safari bottom bar */}
                          <div className="pb-safe" style={{ minHeight: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}></div>
                        </div>
                      );
                    } catch (error) {
                      console.error('Error rendering employee table:', error);
                      return (
                        <div className="text-center py-4 text-red-500">
                          Fehler beim Laden der Mitarbeiter-Tabelle
                        </div>
                      );
                    }
                  })()
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    Keine Mitarbeiter-Bestellungen für heute
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600 text-lg">Keine Bestellungen für heute</p>
              <p className="text-gray-500">Bestellungen werden hier angezeigt, sobald sie eingehen.</p>
            </div>
          )}
        </div>
      </div>
    </div>
    <MealOrderingStatusModal
      isOpen={showMealStatusModal}
      departmentId={departmentId}
      requiresPassword
      onClose={() => setShowMealStatusModal(false)}
    />
    <NightOnlyModeModal
      isOpen={showNightOnlyModal}
      departmentId={departmentId}
      nightOnly={nightOnly}
      onClose={() => setShowNightOnlyModal(false)}
      onSaved={result => setNightOnly(Boolean(result?.night_only))}
    />
    </>
  );
};


// Unified Menu & Price Management Tab Component
export const UnifiedMenuManagementTab = ({ breakfastMenu, toppingsMenu, drinksMenu, sweetsMenu, menuCategories = [], onUpdatePrice, onCreateMenuItem, onDeleteMenuItem, fetchMenus, currentDepartment, departmentConfig = { is_breakfast_lunch_enabled: true }, onUpdateRollTypes, onUpdateBreakfastItemVisibility }) => {
  const [showNewDrink, setShowNewDrink] = useState(false);
  const [showNewSweet, setShowNewSweet] = useState(false);
  // Removed showNewBreakfast - no longer needed
  const [showNewTopping, setShowNewTopping] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', price: '', stock_enabled: false, stock_quantity: 0, purchase_grams_per_half: 0 });
  
  const availableRollTypes = departmentConfig.available_roll_types || ['weiss', 'koerner'];
  
  const toggleRollType = (type) => {
    const isActive = availableRollTypes.includes(type);
    if (isActive && availableRollTypes.length <= 1) return; // can't disable last one
    const newTypes = isActive 
      ? availableRollTypes.filter(t => t !== type) 
      : [...availableRollTypes, type];
    onUpdateRollTypes(newTypes);
  };

  const rollTypeLabels = {
    'weiss': 'Helles Brötchen',
    'koerner': 'Körnerbrötchen'
  };

  const toppingLabels = {
    'ruehrei': 'Rührei',
    'spiegelei': 'Spiegelei',
    'eiersalat': 'Eiersalat',
    'salami': 'Salami',
    'schinken': 'Schinken',
    'kaese': 'Käse',
    'butter': 'Butter'
  };

  const startEditItem = (item, category) => {
    setEditingItem({ ...item, category });
    let displayName = '';
    
    if (category === 'breakfast') {
      displayName = item.name || rollTypeLabels[item.roll_type];
    } else if (category === 'toppings') {
      displayName = item.name || toppingLabels[item.topping_type];
    } else {
      displayName = item.name;
    }
    
    setEditForm({ 
      name: displayName,
      price: item.price.toString(),
      stock_enabled: item.stock_enabled || false,
      stock_quantity: item.stock_quantity || 0,
      purchase_grams_per_half: item.purchase_grams_per_half || 0
    });
  };

  const saveEdit = async () => {
    if (!editingItem) return;

    try {
      if (!currentDepartment?.department_id) {
        alert('Fehler: Keine Abteilungs-ID verfügbar');
        return;
      }

      const updateData = {
        name: editForm.name,
        price: parseFloat(editForm.price)
      };
      
      // Add stock fields for drinks and sweets
      if (editingItem.category === 'drinks' || editingItem.category === 'sweets') {
        updateData.stock_enabled = editForm.stock_enabled;
        updateData.stock_quantity = parseInt(editForm.stock_quantity) || 0;
      }
      
      // Add purchase grams for toppings
      if (editingItem.category === 'toppings') {
        updateData.purchase_grams_per_half = parseInt(editForm.purchase_grams_per_half) || 0;
      }
      
      const departmentId = currentDepartment.department_id;
      await axios.put(`${API}/department-admin/menu/${editingItem.category}/${editingItem.id}?department_id=${departmentId}`, updateData);
      
      fetchMenus();
      setEditingItem(null);
      setEditForm({ name: '', price: '', stock_enabled: false, stock_quantity: 0, purchase_grams_per_half: 0 });
      alert('Artikel erfolgreich aktualisiert');
    } catch (error) {
      console.error('Fehler beim Aktualisieren:', error);
      alert('Fehler beim Aktualisieren');
    }
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setEditForm({ name: '', price: '', stock_enabled: false, stock_quantity: 0, purchase_grams_per_half: 0 });
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Menü & Preise</h3>
        <p className="mt-1 text-sm text-gray-500">Produkte, Preise und Lagerbestände übersichtlich verwalten.</p>
      </div>

      <div className="space-y-8">
        {/* Breakfast Items - only show if breakfast is enabled */}
        {departmentConfig.is_breakfast_lunch_enabled && (
          <>
            {/* Breakfast Items - Simplified Price Management */}
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h4 className="mb-4 text-base font-semibold text-gray-900">Brötchen</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {breakfastMenu.map((item) => {
              const rollType = item.roll_type || item.id;
              const isVisible = availableRollTypes.includes(rollType);
              const isLastActive = isVisible && availableRollTypes.length <= 1;
              return (
              <div key={item.id} className={`relative rounded-lg border p-4 transition-all ${isVisible ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-60'}`}>
                {/* Eye Toggle */}
                <button
                  onClick={() => toggleRollType(rollType)}
                  className={`absolute top-2 right-2 p-1.5 rounded-full transition-colors ${isLastActive ? 'cursor-not-allowed opacity-30' : 'hover:bg-gray-200'}`}
                  disabled={isLastActive}
                  title={isVisible ? 'Deaktivieren' : 'Aktivieren'}
                  data-testid={`roll-toggle-${rollType}`}
                >
                  {isVisible ? (
                    <Eye className="h-4 w-4 text-slate-700" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  )}
                </button>
                {editingItem?.id === item.id ? (
                  <div className="space-y-3">
                    <div className="font-medium text-gray-700">{item.name || rollTypeLabels[item.roll_type]}</div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Preis (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={editForm.price}
                        onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={saveEdit} className="rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700">
                        Speichern
                      </button>
                      <button onClick={cancelEdit} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                        Abbrechen
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <div className="font-medium text-gray-900">{item.name || rollTypeLabels[item.roll_type]}</div>
                      <div className="mt-1 text-lg font-semibold text-gray-900">{item.price.toFixed(2)} €</div>
                    </div>
                    <button
                      onClick={() => startEditItem(item, 'breakfast')}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Bearbeiten
                    </button>
                  </div>
                )}
              </div>
            );
            })}
          </div>
        </div>

        {/* Toppings */}
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-base font-semibold text-gray-900">Beläge</h4>
            <button
              onClick={() => setShowNewTopping(true)}
              className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
            >
              Neuer Belag
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {toppingsMenu.map((item) => (
              <div key={item.id} className="rounded-lg border border-gray-200 bg-white p-4">
                {editingItem?.id === item.id ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Name</label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Preis (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={editForm.price}
                        onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded"
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium mb-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editForm.purchase_grams_per_half > 0}
                          onChange={(e) => setEditForm(prev => ({ ...prev, purchase_grams_per_half: e.target.checked ? (prev.purchase_grams_per_half || 50) : 0 }))}
                          className="rounded border-gray-300"
                          data-testid="topping-purchase-toggle"
                        />
                        Einkaufsmenge berechnen
                      </label>
                      {editForm.purchase_grams_per_half > 0 && (
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="number"
                            min="1"
                            value={editForm.purchase_grams_per_half}
                            onChange={(e) => setEditForm(prev => ({ ...prev, purchase_grams_per_half: parseInt(e.target.value) || 0 }))}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            data-testid="topping-purchase-grams"
                          />
                          <span className="text-sm text-gray-500">g pro Hälfte</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={saveEdit} className="rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700">
                        Speichern
                      </button>
                      <button onClick={cancelEdit} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                        Abbrechen
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <div className="font-medium text-gray-900">{item.name || toppingLabels[item.topping_type]}</div>
                      <div className="mt-1 text-lg font-semibold text-gray-900">{item.price.toFixed(2)} €</div>
                      {item.purchase_grams_per_half > 0 && (
                        <div className="mt-1 inline-block rounded bg-gray-100 px-2 py-1 text-xs text-gray-600" data-testid="topping-grams-badge">
                          {item.purchase_grams_per_half}g / Hälfte
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEditItem(item, 'toppings')}
                        className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => onDeleteMenuItem('toppings', item.id)}
                        className="rounded-md px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                      >
                        Löschen
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Eier & Kaffee */}
        <CoffeeAndEggsManagement
          currentDepartment={currentDepartment}
          breakfastItemVisibility={departmentConfig.breakfast_item_visibility}
          onUpdateBreakfastItemVisibility={onUpdateBreakfastItemVisibility}
        />
          </>
        )}

        {/* Drinks */}
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-base font-semibold text-gray-900">Getränke</h4>
            <button
              onClick={() => setShowNewDrink(true)}
              className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
            >
              Neues Getränk
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {drinksMenu.map((item) => (
              <div key={item.id} className="rounded-lg border border-gray-200 bg-white p-4">
                {editingItem?.id === item.id ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Name</label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Preis (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={editForm.price}
                        onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded"
                      />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={editForm.stock_enabled}
                        onChange={(e) => setEditForm(prev => ({ ...prev, stock_enabled: e.target.checked }))}
                        className="rounded border-gray-300"
                      />
                      Lagerbestand verwenden
                    </label>
                    {editForm.stock_enabled && (
                      <div>
                        <label className="block text-sm font-medium mb-1">Bestand</label>
                        <input
                          type="number"
                          min="0"
                          value={editForm.stock_quantity}
                          onChange={(e) => setEditForm(prev => ({ ...prev, stock_quantity: e.target.value }))}
                          className="w-full rounded border border-gray-300 px-3 py-2"
                        />
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button onClick={saveEdit} className="rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700">
                        Speichern
                      </button>
                      <button onClick={cancelEdit} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                        Abbrechen
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <div className="font-medium text-gray-900">{item.name}</div>
                      <div className="mt-1 text-lg font-semibold text-gray-900">{item.price.toFixed(2)} €</div>
                      <div className="mt-2 text-xs text-gray-500">
                        {item.stock_enabled ? `Bestand: ${item.stock_quantity}` : 'Unbegrenzter Bestand'}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEditItem(item, 'drinks')}
                        className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => onDeleteMenuItem('drinks', item.id)}
                        className="rounded-md px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                      >
                        Löschen
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>


        {/* Sweets */}
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-base font-semibold text-gray-900">Snacks</h4>
            <button
              onClick={() => setShowNewSweet(true)}
              className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
            >
              Neuer Snack
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sweetsMenu.map((item) => (
              <div key={item.id} className="rounded-lg border border-gray-200 bg-white p-4">
                {editingItem?.id === item.id ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Name</label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Preis (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={editForm.price}
                        onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded"
                      />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={editForm.stock_enabled}
                        onChange={(e) => setEditForm(prev => ({ ...prev, stock_enabled: e.target.checked }))}
                        className="rounded border-gray-300"
                      />
                      Lagerbestand verwenden
                    </label>
                    {editForm.stock_enabled && (
                      <div>
                        <label className="block text-sm font-medium mb-1">Bestand</label>
                        <input
                          type="number"
                          min="0"
                          value={editForm.stock_quantity}
                          onChange={(e) => setEditForm(prev => ({ ...prev, stock_quantity: e.target.value }))}
                          className="w-full rounded border border-gray-300 px-3 py-2"
                        />
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button onClick={saveEdit} className="rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700">
                        Speichern
                      </button>
                      <button onClick={cancelEdit} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                        Abbrechen
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <div className="font-medium text-gray-900">{item.name}</div>
                      <div className="mt-1 text-lg font-semibold text-gray-900">{item.price.toFixed(2)} €</div>
                      <div className="mt-2 text-xs text-gray-500">
                        {item.stock_enabled ? `Bestand: ${item.stock_quantity}` : 'Unbegrenzter Bestand'}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEditItem(item, 'sweets')}
                        className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => onDeleteMenuItem('sweets', item.id)}
                        className="rounded-md px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                      >
                        Löschen
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <CustomMenuCategories
        categories={menuCategories.filter(category => !category.is_system)}
        currentDepartment={currentDepartment}
        departmentConfig={departmentConfig}
        fetchMenus={fetchMenus}
      />
      {showNewDrink && (
        <NewMenuItemModal
          title="Neues Getränk hinzufügen"
          onCreateItem={async (name, price) => {
            if (await onCreateMenuItem('drinks', name, price)) setShowNewDrink(false);
          }}
          onClose={() => setShowNewDrink(false)}
        />
      )}

      {showNewSweet && (
        <NewMenuItemModal
          title="Neuen Snack hinzufügen"
          onCreateItem={async (name, price) => {
            if (await onCreateMenuItem('sweets', name, price)) setShowNewSweet(false);
          }}
          onClose={() => setShowNewSweet(false)}
        />
      )}

      {showNewTopping && (
        <NewToppingItemModal
          title="Neuer Belag hinzufügen"
          onCreateItem={async (toppingId, toppingName, price, purchaseGrams) => {
            if (await onCreateMenuItem('toppings', toppingId, toppingName, price, purchaseGrams)) setShowNewTopping(false);
          }}
          onClose={() => setShowNewTopping(false)}
        />
      )}
    </div>
  );
};

const CustomMenuCategories = ({ categories, currentDepartment, departmentConfig, fetchMenus }) => {
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [postingAccount, setPostingAccount] = useState('drinks');
  const [newProductCategory, setNewProductCategory] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({ name: '', price: '', stock_enabled: false, stock_quantity: 0 });

  const departmentId = currentDepartment?.department_id;
  const accountLabel = (account) => {
    if (departmentConfig.use_combined_accounts) return 'Hauptkonto';
    return account === 'breakfast' ? 'Frühstück & Essen' : 'Getränke & Snacks';
  };

  const createCategory = async (event) => {
    event.preventDefault();
    try {
      await axios.post(`${API}/department-admin/menu/categories/${departmentId}`, {
        name: categoryName,
        posting_account: postingAccount,
      });
      await fetchMenus();
      setCategoryName('');
      setPostingAccount('drinks');
      setShowCategoryForm(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Kategorie konnte nicht erstellt werden');
    }
  };

  const updateCategory = async (category, fields) => {
    try {
      await axios.put(
        `${API}/department-admin/menu/categories/${departmentId}/${category.id}`,
        fields
      );
      await fetchMenus();
    } catch (error) {
      alert(error.response?.data?.detail || 'Kategorie konnte nicht aktualisiert werden');
    }
  };

  const deleteCategory = async (category) => {
    if (!window.confirm(`Kategorie „${category.name}“ wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/department-admin/menu/categories/${departmentId}/${category.id}`);
      await fetchMenus();
    } catch (error) {
      alert(error.response?.data?.detail || 'Kategorie konnte nicht gelöscht werden');
    }
  };

  const createProduct = async (name, price) => {
    try {
      await axios.post(
        `${API}/department-admin/menu/categories/${departmentId}/${newProductCategory.id}/products`,
        { name, price: parseFloat(price), stock_enabled: false, stock_quantity: 0 }
      );
      await fetchMenus();
      setNewProductCategory(null);
      return true;
    } catch (error) {
      alert(error.response?.data?.detail || 'Produkt konnte nicht erstellt werden');
      return false;
    }
  };

  const startProductEdit = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      price: String(product.price),
      stock_enabled: Boolean(product.stock_enabled),
      stock_quantity: product.stock_quantity || 0,
    });
  };

  const saveProduct = async () => {
    try {
      await axios.put(
        `${API}/department-admin/menu/category-products/${departmentId}/${editingProduct.id}`,
        {
          ...productForm,
          price: parseFloat(productForm.price),
          stock_quantity: parseInt(productForm.stock_quantity, 10) || 0,
        }
      );
      await fetchMenus();
      setEditingProduct(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Produkt konnte nicht aktualisiert werden');
    }
  };

  const deleteProduct = async (product) => {
    if (!window.confirm(`Produkt „${product.name}“ wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/department-admin/menu/category-products/${departmentId}/${product.id}`);
      await fetchMenus();
    } catch (error) {
      alert(error.response?.data?.detail || 'Produkt konnte nicht gelöscht werden');
    }
  };

  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h4 className="text-base font-semibold text-gray-900">Weitere Kategorien</h4>
          <p className="text-sm text-gray-500 mt-1">Eigene Bereiche für Eis, Süßigkeiten oder weitere Produkte.</p>
        </div>
        <button
          type="button"
          onClick={() => setShowCategoryForm(true)}
          className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
        >
          Kategorie hinzufügen
        </button>
      </div>

      {categories.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 px-5 py-7 text-sm text-gray-500">
          Noch keine zusätzlichen Kategorien angelegt.
        </div>
      ) : (
        <div className="space-y-6">
          {categories.map(category => (
            <div key={category.id} className={`rounded-xl border p-5 ${category.is_archived ? 'border-gray-200 bg-gray-50 opacity-75' : 'border-slate-200 bg-white'}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h5 className="font-semibold text-gray-900">{category.name}</h5>
                    {category.is_archived && <span className="text-xs text-gray-500">Archiviert</span>}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Buchung auf: {accountLabel(category.posting_account)}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {!category.is_archived && (
                    <button onClick={() => setNewProductCategory(category)} className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-700">
                      Produkt hinzufügen
                    </button>
                  )}
                  <button onClick={() => updateCategory(category, { is_archived: !category.is_archived })} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                    {category.is_archived ? 'Wiederherstellen' : 'Archivieren'}
                  </button>
                  {!category.has_orders && (
                    <button onClick={() => deleteCategory(category)} className="rounded-md px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50">
                      Löschen
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {(category.products || []).map(product => (
                  <div key={product.id} className="rounded-lg border border-gray-200 p-4">
                    {editingProduct?.id === product.id ? (
                      <div className="space-y-3">
                        <input value={productForm.name} onChange={event => setProductForm(prev => ({ ...prev, name: event.target.value }))} className="w-full rounded-md border px-3 py-2 text-sm" />
                        <input type="number" min="0" step="0.01" value={productForm.price} onChange={event => setProductForm(prev => ({ ...prev, price: event.target.value }))} className="w-full rounded-md border px-3 py-2 text-sm" />
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                          <input type="checkbox" checked={productForm.stock_enabled} onChange={event => setProductForm(prev => ({ ...prev, stock_enabled: event.target.checked }))} />
                          Lagerbestand verwenden
                        </label>
                        {productForm.stock_enabled && (
                          <input type="number" min="0" value={productForm.stock_quantity} onChange={event => setProductForm(prev => ({ ...prev, stock_quantity: event.target.value }))} className="w-full rounded-md border px-3 py-2 text-sm" />
                        )}
                        <div className="flex gap-2">
                          <button onClick={saveProduct} className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-medium text-white">Speichern</button>
                          <button onClick={() => setEditingProduct(null)} className="rounded-md border px-3 py-1.5 text-xs">Abbrechen</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between gap-3">
                          <div className="font-medium text-gray-900">{product.name}</div>
                          <div className="font-semibold text-gray-900">{Number(product.price).toFixed(2)} €</div>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          {product.stock_enabled ? `Bestand: ${product.stock_quantity}` : 'Unbegrenzter Bestand'}
                        </div>
                        <div className="mt-4 flex gap-2">
                          <button onClick={() => startProductEdit(product)} className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">Bearbeiten</button>
                          <button onClick={() => deleteProduct(product)} className="rounded-md px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50">Löschen</button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCategoryForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <form onSubmit={createCategory} className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Kategorie hinzufügen</h3>
            <label className="mt-5 block text-sm font-medium text-gray-700">Name</label>
            <input autoFocus value={categoryName} onChange={event => setCategoryName(event.target.value)} className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2" required />
            <label className="mt-4 block text-sm font-medium text-gray-700">Buchungskonto</label>
            <select value={postingAccount} onChange={event => setPostingAccount(event.target.value)} className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2">
              <option value="drinks">Getränke & Snacks</option>
              <option value="breakfast">Frühstück & Essen</option>
            </select>
            {departmentConfig.use_combined_accounts && (
              <p className="mt-2 text-xs text-gray-500">Aktuell wird alles auf das Hauptkonto gebucht. Die Zuordnung bleibt für eine spätere Kontentrennung gespeichert.</p>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setShowCategoryForm(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Abbrechen</button>
              <button type="submit" className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white">Erstellen</button>
            </div>
          </form>
        </div>
      )}

      {newProductCategory && (
        <NewMenuItemModal
          title={`Neues Produkt in ${newProductCategory.name}`}
          onCreateItem={createProduct}
          onClose={() => setNewProductCategory(null)}
        />
      )}
    </section>
  );
};

// Extended Order History Tab Component
export const ExtendedOrderHistoryTab = ({
  extendedOrderHistory,
  historyPagination = { page: 1, page_size: 30, total: 0, total_pages: 1 },
  fetchExtendedOrderHistory,
  selectedDeptForHistory,
  setSelectedDeptForHistory,
  currentDepartment,
}) => {
  const [departments, setDepartments] = useState([]);
  
  useEffect(() => {
    // If currentDepartment is provided (Admin Dashboard), use it directly
    if (currentDepartment) {
      fetchExtendedOrderHistory(1, currentDepartment.department_id);
      return;
    }
    
    // Otherwise, fetch all departments (Developer Dashboard)
    fetchDepartments();
  }, []);
  
  useEffect(() => {
    // Auto-select first department on first load (Developer Dashboard only)
    if (!currentDepartment && !selectedDeptForHistory && departments.length > 0) {
      setSelectedDeptForHistory(departments[0].id);
      fetchExtendedOrderHistory(1, departments[0].id);
    }
  }, [departments]);
  
  useEffect(() => {
    // Fetch when department changes (Developer Dashboard only)
    if (!currentDepartment && selectedDeptForHistory) {
      fetchExtendedOrderHistory(1, selectedDeptForHistory);
    }
  }, [selectedDeptForHistory]);
  
  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Wachabteilungen:', error);
    }
  };
  
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Berlin'
    });
  };
  
  const getOrderTypeColor = (type) => {
    switch (type) {
      case 'Frühstück':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'Mittagessen':
        return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'Abendessen':
        return 'bg-indigo-100 text-indigo-800 border-indigo-300';
      case 'Getränke':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Snacks':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };
  
  const mealTypePresentation = {
    breakfast: { label: 'Frühstück', className: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
    lunch: { label: 'Mittagessen', className: 'bg-amber-50 text-amber-800 border-amber-200' },
    dinner: { label: 'Abendessen', className: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
  };

  const requestPage = (page) => {
    const departmentId = currentDepartment?.department_id || selectedDeptForHistory;
    if (!departmentId || page < 1 || page > historyPagination.total_pages) return;
    fetchExtendedOrderHistory(page, departmentId);
  };

  const firstVisibleOrder = historyPagination.total === 0
    ? 0
    : ((historyPagination.page - 1) * historyPagination.page_size) + 1;
  const lastVisibleOrder = Math.min(
    historyPagination.page * historyPagination.page_size,
    historyPagination.total,
  );

  return (
    <div>
      <h3 className="text-2xl font-bold mb-6">Erweiterter Bestellverlauf</h3>
      
      {/* Department Selector - Only show in Developer Dashboard */}
      {!currentDepartment && (
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Wachabteilung wählen:</label>
          <select
            value={selectedDeptForHistory}
            onChange={(e) => setSelectedDeptForHistory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
          >
            <option value="">Bitte wählen...</option>
            {departments.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </select>
        </div>
      )}
      
      {/* Show department name in Admin Dashboard */}
      {currentDepartment && (
        <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Wachabteilung</p>
            <p className="mt-0.5 font-semibold text-slate-900">{currentDepartment.department_name}</p>
          </div>
          <p className="mt-2 text-sm tabular-nums text-slate-600 sm:mt-0">
            {firstVisibleOrder}-{lastVisibleOrder} von {historyPagination.total} Buchungen
          </p>
        </div>
      )}
      
      {/* Order History Feed */}
      {extendedOrderHistory.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <p className="text-gray-600">Keine Bestellungen vorhanden</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-col gap-1 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
            <span>Neueste Buchungen zuerst, begrenzt auf 150 Einträge</span>
            <span className="font-medium tabular-nums text-slate-800">
              Seite {historyPagination.page} von {historyPagination.total_pages}
            </span>
          </div>
          
          {extendedOrderHistory.map((order) => (
            <div 
              key={order.order_id} 
              className={`rounded-xl border p-4 ${
                order.is_cancelled 
                  ? (order.cancellation_reason === 'reset_for_edit' ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50')
                  : 'border-slate-200 bg-white'
              } shadow-sm`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-lg">{order.employee_name}</h4>
                    
                    {/* Employee Marker */}
                    {order.employee_marker && (
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        order.is_8h_service 
                          ? 'bg-orange-100 text-orange-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {order.is_8h_service ? 'Übergreifend' : `👥 ${order.employee_marker}`}
                      </span>
                    )}
                    
                    {/* Actual meal badges for combined food orders */}
                    {order.order_details?.meal_types?.length > 0 ? (
                      order.order_details.meal_types.map(mealType => {
                        const presentation = mealTypePresentation[mealType];
                        if (!presentation) return null;
                        return (
                          <span key={mealType} className={`rounded-md border px-2 py-1 text-xs font-medium ${presentation.className}`}>
                            {presentation.label}
                          </span>
                        );
                      })
                    ) : (
                      <span className={`rounded-md border px-2 py-1 text-xs font-medium ${getOrderTypeColor(order.order_details.type)}`}>
                        {order.order_details.type}
                      </span>
                    )}
                    
                    {/* Cancelled Badge */}
                    {order.is_cancelled && (
                      <span className={`text-xs px-2 py-1 rounded ${
                        order.cancellation_reason === 'reset_for_edit' 
                          ? 'bg-amber-100 text-amber-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {order.cancellation_reason === 'reset_for_edit' ? 'Zum Bearbeiten zurückgesetzt' : 'Storniert'}
                      </span>
                    )}
                    
                    {/* Sponsored Badge */}
                    {order.is_sponsored && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                        🎁 Gesponsert
                      </span>
                    )}
                    
                    {/* Sponsor Order Badge */}
                    {order.is_sponsor_order && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                        💰 Sponsor
                      </span>
                    )}
                  </div>
                  
                  <div className="text-sm text-gray-600 mt-1">
                    {formatDate(order.timestamp)}
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-lg font-bold text-gray-800">
                    {order.order_details?.total_price ? order.order_details.total_price.toFixed(2) : '0.00'} €
                  </div>
                </div>
              </div>
              
              {/* Order Items */}
              <div className="mt-3 grid gap-1.5 rounded-lg bg-slate-50 px-3 py-2.5 sm:grid-cols-2">
                {order.order_details?.items && order.order_details.items.length > 0 ? (
                  order.order_details.items.map((item, index) => (
                    <div key={index} className="flex gap-2 text-sm text-slate-700">
                      <span className="text-slate-400" aria-hidden="true">•</span>
                      <span>{item}</span>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-gray-500 italic">Keine Details verfügbar</div>
                )}
              </div>
              <OrderEditHistory entries={order.edit_history} />
            </div>
          ))}

          {historyPagination.total_pages > 1 && (
            <nav
              className="mt-6 flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between"
              aria-label="Seiten des Bestellverlaufs"
            >
              <button
                type="button"
                onClick={() => requestPage(historyPagination.page - 1)}
                disabled={historyPagination.page <= 1}
                className="min-h-10 rounded-lg border border-slate-300 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Zurück
              </button>
              <div className="flex items-center justify-center gap-1.5">
                {Array.from({ length: historyPagination.total_pages }, (_, index) => index + 1).map(page => (
                  <button
                    key={page}
                    type="button"
                    data-history-page={page}
                    onClick={() => requestPage(page)}
                    aria-current={page === historyPagination.page ? 'page' : undefined}
                    className={`h-10 min-w-10 rounded-lg px-3 text-sm font-semibold transition-colors ${
                      page === historyPagination.page
                        ? 'bg-slate-900 text-white'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => requestPage(historyPagination.page + 1)}
                disabled={historyPagination.page >= historyPagination.total_pages}
                className="min-h-10 rounded-lg border border-slate-300 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Weiter
              </button>
            </nav>
          )}
        </div>
      )}
    </div>
  );
};

// Breakfast History Tab Component
export const BreakfastHistoryTab = ({ currentDepartment, departmentConfig = {} }) => {
  const [breakfastHistory, setBreakfastHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [defaultDinnerPrice, setDefaultDinnerPrice] = useState(null);
  const dinnerEnabled = departmentConfig?.meal_types?.dinner?.enabled !== false;
  const [separatedRevenue, setSeparatedRevenue] = useState({
    breakfast_revenue: 0,
    lunch_revenue: 0
  });
  const [dailyRevenues, setDailyRevenues] = useState({}); // Store daily revenue data
  const [defaultLunchPrice, setDefaultLunchPrice] = useState(null); // Department default lunch price for fallback hint
  // Phase 2: variants modal
  const [variantsModal, setVariantsModal] = useState(null); // { date, meal_type } or null
  const [variantsDraft, setVariantsDraft] = useState([]);
  const [variantsInitialCount, setVariantsInitialCount] = useState(0);
  const [variantsSaving, setVariantsSaving] = useState(false);

  const openVariantsModal = async (date, mealType) => {
    setVariantsModal({ date, meal_type: mealType });
    setVariantsDraft([]);
    setVariantsInitialCount(0);
    try {
      const res = await axios.get(`${API}/daily-meal-variants/${currentDepartment.department_id}/${date}/${mealType}`);
      const list = res.data.variants || [];
      const cleaned = list.map(v => ({
        name: v.name,
        price: v.price,
      }));
      setVariantsInitialCount(cleaned.length);
      setVariantsDraft(cleaned.length ? cleaned : [{ name: '', price: '' }]);
    } catch (e) {
      setVariantsInitialCount(0);
      setVariantsDraft([{ name: '', price: '' }]);
    }
  };

  const saveVariants = async () => {
    if (!variantsModal) return;
    setVariantsSaving(true);
    try {
      const clean = variantsDraft
        .map(v => ({
          name: (v.name || '').trim(),
          price: v.price === '' || v.price == null ? null : Number(v.price),
        }))
        .filter(v => v.name || v.price !== null);
      if (clean.length === 0 || clean.some(v => !v.name || (v.price === null ? clean.length > 1 : !Number.isFinite(v.price) || v.price < 0))) {
        alert('Bitte einen Namen und bei mehreren Essen für jede Variante einen gültigen Preis eingeben.');
        return;
      }
      if (variantsModal.meal_type === 'lunch') {
        await axios.put(
          `${API}/daily-lunch-pricing/${currentDepartment.department_id}/${variantsModal.date}`,
          { variants: clean }
        );
      } else if (variantsModal.meal_type === 'dinner') {
        await axios.put(
          `${API}/daily-dinner-pricing/${currentDepartment.department_id}/${variantsModal.date}`,
          { variants: clean }
        );
      } else {
        await axios.put(
          `${API}/daily-meal-variants/${currentDepartment.department_id}/${variantsModal.date}/${variantsModal.meal_type}`,
          { variants: clean }
        );
      }
      setVariantsModal(null);
      await fetchBreakfastHistory();
      await fetchSeparatedRevenue();
    } catch (e) {
      alert(e.response?.data?.detail || 'Speichern fehlgeschlagen.');
    } finally {
      setVariantsSaving(false);
    }
  };
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Sponsoring Modal State
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [sponsorModalData, setSponsorModalData] = useState({
    mealType: '',
    date: '',
  });
  const [departmentEmployees, setDepartmentEmployees] = useState([]);
  const [dailySponsorStatus, setDailySponsorStatus] = useState({});
  const [showMealStatusModal, setShowMealStatusModal] = useState(false);

  useEffect(() => {
    fetchBreakfastHistory();
    fetchDepartmentEmployees();
    fetchSeparatedRevenue();
    fetchDefaultLunchPrice();
  }, [currentDepartment]);

  const fetchDefaultLunchPrice = async () => {
    try {
      const res = await axios.get(`${API}/department-settings/${currentDepartment.department_id}`);
      setDefaultLunchPrice(res.data?.default_lunch_price ?? null);
      setDefaultDinnerPrice(res.data?.default_dinner_price ?? null);
    } catch (err) {
      setDefaultLunchPrice(null);
      setDefaultDinnerPrice(null);
    }
  };

  const fetchBreakfastHistory = async ({quiet = false} = {}) => {
    try {
      if (!quiet) setLoading(true);
      const response = await axios.get(`${API}/orders/breakfast-history/${currentDepartment.department_id}?days_back=30`);
      setBreakfastHistory(response.data.history || []);
      // Load sponsor status for all days
      if (response.data.history && response.data.history.length > 0) {
        fetchSponsorStatusForDays(response.data.history);
      }
    } catch (error) {
      console.error('Fehler beim Laden des Essensverlaufs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSeparatedRevenue = async () => {
    try {
      const response = await axios.get(`${API}/orders/separated-revenue/${currentDepartment.department_id}?days_back=30`);
      setSeparatedRevenue(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der getrennten Umsätze:', error);
      setSeparatedRevenue({
        breakfast_revenue: 0,
        lunch_revenue: 0
      });
    }
  };

  const fetchDailyRevenue = async (date) => {
    try {
      const response = await axios.get(`${API}/orders/daily-revenue/${currentDepartment.department_id}/${date}`);
      setDailyRevenues(prev => ({
        ...prev,
        [date]: response.data
      }));
      return response.data;
    } catch (error) {
      console.error(`Fehler beim Laden der Tagesumsätze für ${date}:`, error);
      return {
        breakfast_revenue: 0,
        lunch_revenue: 0,
        total_revenue: 0,
        total_orders: 0
      };
    }
  };

  // Load daily revenues for visible days
  useEffect(() => {
    const fetchDailyRevenues = async () => {
      if (breakfastHistory.length > 0) {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const currentPageDays = breakfastHistory.slice(startIndex, endIndex);
        
        // Fetch revenue data for all days on current page
        await Promise.all(
          currentPageDays.map(day => fetchDailyRevenue(day.date))
        );
      }
    };
    
    fetchDailyRevenues();
  }, [breakfastHistory, currentPage, currentDepartment]);

  const handleSponsorMeal = (selectedEmployee, mealType, date) => {
    setSponsorModalData({mealType, date, initialSponsor: selectedEmployee});
    setShowSponsorModal(true);
  };

  const refreshAfterSponsoring = async () => {
    await Promise.all([
      fetchBreakfastHistory({quiet: true}),
      fetchSeparatedRevenue(),
      fetchSponsorStatusForDays([{date: sponsorModalData.date}]),
    ]);
  };

  const fetchDepartmentEmployees = async () => {
    try {
      console.log('🔄 Lade Mitarbeiter für Sponsoring...');
      
      // Fetch regular employees
      const response = await axios.get(`${API}/departments/${currentDepartment.department_id}/employees`);
      let allEmps = response.data;
      console.log('📋 Reguläre Mitarbeiter geladen:', allEmps.length);
      
      // Fetch 8H-Service employees
      try {
        const eightHResponse = await axios.get(`${API}/departments/${currentDepartment.department_id}/8h-employees`);
        const eightHEmployees = eightHResponse.data.map(emp => ({
          id: emp.id,
          name: emp.name,
          department_id: emp.department_id,
          is_8h_service: true
        }));
        console.log('🕐 8H-Mitarbeiter geladen:', eightHEmployees.length);
        allEmps = [...allEmps, ...eightHEmployees];
      } catch (eightHError) {
        console.error('❌ Fehler beim Laden der 8H-Mitarbeiter:', eightHError);
      }
      
      console.log('✅ Gesamt Mitarbeiter für Sponsoring:', allEmps.length);
      setDepartmentEmployees(allEmps);
    } catch (error) {
      console.error('Fehler beim Laden der Abteilungs-Mitarbeiter:', error);
    }
  };

  const fetchSponsorStatusForDays = async (days) => {
    try {
      const statusPromises = days.map(day => 
        axios.get(`${API}/department-admin/sponsor-status/${currentDepartment.department_id}/${day.date}`)
          .then(response => ({ date: day.date, status: response.data }))
          .catch(error => {
            console.error(`Fehler beim Laden des Sponsor-Status für ${day.date}:`, error);
            return { date: day.date, status: { breakfast_sponsored: null, lunch_sponsored: null } };
          })
      );
      
      const statusResults = await Promise.all(statusPromises);
      const statusMap = {};
      statusResults.forEach(result => {
        statusMap[result.date] = result.status;
      });
      
      setDailySponsorStatus(previous => ({ ...previous, ...statusMap }));
    } catch (error) {
      console.error('Fehler beim Laden der Sponsor-Stati:', error);
    }
  };

  const deleteBreakfastDay = async (date) => {
    if (window.confirm(`Alle Frühstücks-Bestellungen für ${formatDate(date)} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden und wird die Mitarbeiter-Salden entsprechend anpassen.`)) {
      try {
        setDeleting(date);
        await axios.delete(`${API}/department-admin/breakfast-day/${currentDepartment.department_id}/${date}`);
        alert('Frühstücks-Tag erfolgreich gelöscht');
        // Refresh the history
        await fetchBreakfastHistory();
      } catch (error) {
        console.error('Fehler beim Löschen des Frühstücks-Tags:', error);
        alert('Fehler beim Löschen des Frühstücks-Tags');
      } finally {
        setDeleting(null);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'long'
    });
  };

  // Phase 3: Reset today's shift state (admin only)
  const resetTodayShift = async () => {
    const today = getBerlinDate();
    if (!window.confirm(
      `Achtung: Damit werden ALLE Bestellungen vom ${formatDate(today)} gelöscht.\n\nFortfahren?`
    )) return;
    try {
      const res = await axios.delete(`${API}/shift-state/${currentDepartment.department_id}/${today}`);
      alert(`Tag zurückgesetzt. ${res.data.orders_deleted} Bestellungen entfernt.`);
      await fetchBreakfastHistory();
      await fetchSeparatedRevenue();
    } catch (e) {
      alert(e.response?.data?.detail || 'Reset fehlgeschlagen.');
    }
  };

  // Phase 3: Add a future day (empty planning slot) — Modal mit Date-Picker (mobil: native Rad-UI)
  const [showAddDayModal, setShowAddDayModal] = useState(false);
  const [addDayValue, setAddDayValue] = useState('');
  const [addDayNightOnly, setAddDayNightOnly] = useState(false);
  const [addDaySaving, setAddDaySaving] = useState(false);
  const [nightOnlySavingDate, setNightOnlySavingDate] = useState(null);

  const openAddDayModal = () => {
    const today = getBerlinDate();
    setAddDayValue(today);
    setAddDayNightOnly(false);
    setShowAddDayModal(true);
  };
  const closeAddDayModal = () => {
    setShowAddDayModal(false);
    setAddDayValue('');
    setAddDayNightOnly(false);
    setAddDaySaving(false);
  };
  const submitAddDay = async () => {
    if (!addDayValue || !/^\d{4}-\d{2}-\d{2}$/.test(addDayValue)) {
      alert('Bitte ein gültiges Datum wählen.');
      return;
    }
    try {
      setAddDaySaving(true);
      await axios.post(
        `${API}/daily-meal-plans/${currentDepartment.department_id}`,
        buildDailyMealPlanPayload(addDayValue, addDayNightOnly)
      );
      await fetchBreakfastHistory();
      closeAddDayModal();
    } catch (e) {
      alert(e.response?.data?.detail || 'Tag anlegen fehlgeschlagen.');
      setAddDaySaving(false);
    }
  };

  const updateNightOnly = async (date, nightOnly) => {
    try {
      setNightOnlySavingDate(date);
      const response = await axios.put(
        `${API}/department-admin/daily-night-only/${currentDepartment.department_id}/${date}`,
        { night_only: nightOnly }
      );
      setBreakfastHistory(current => applyNightOnlyHistoryUpdate(
        current,
        date,
        response.data?.night_only
      ));
    } catch (error) {
      alert(error.response?.data?.detail || 'Nachtdienst-Modus konnte nicht gespeichert werden.');
    } finally {
      setNightOnlySavingDate(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Lade Essensverlauf...</div>
      </div>
    );
  }

  // Pagination logic
  const totalPages = Math.ceil(breakfastHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = breakfastHistory.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToFirstPage = () => {
    setCurrentPage(1);
  };

  return (
    <div className="py-6">
      {/* Add-Day Modal */}
      {showAddDayModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeAddDayModal}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="add-meal-plan-day-modal"
          >
            <h3 className="text-lg font-bold text-gray-900 mb-2">Neuen Tag anlegen</h3>
            <p className="text-xs text-gray-500 mb-4">
              Wähle das Datum. Ein leerer Tag wird im Essensverlauf angelegt, sodass du Varianten und Preise im Voraus pflegen kannst.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Datum</label>
            <input
              type="date"
              value={addDayValue}
              onChange={(e) => setAddDayValue(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="add-meal-plan-day-input"
              autoFocus
            />
            <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-3">
              <input
                type="checkbox"
                checked={addDayNightOnly}
                onChange={event => setAddDayNightOnly(event.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
                data-testid="add-meal-plan-night-only"
              />
              <span>
                <span className="block text-sm font-semibold text-indigo-950">Nur Nachtdienst</span>
                <span className="block text-xs text-indigo-700">Frühstück und Mittag werden an diesem Tag im Bestellformular ausgeblendet.</span>
              </span>
            </label>
            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={closeAddDayModal}
                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={submitAddDay}
                disabled={addDaySaving || !addDayValue}
                data-testid="add-meal-plan-day-submit"
                className="flex-1 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:bg-gray-400"
              >
                {addDaySaving ? 'Speichern…' : 'Tag anlegen'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h3 className="text-lg font-semibold">Essensverlauf - {currentDepartment.department_name}</h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowMealStatusModal(true)}
            data-testid="meal-ordering-status-button"
            className="px-3 py-1.5 text-sm rounded-lg bg-slate-900 hover:bg-slate-800 text-white border border-slate-900"
          >
            Bestellungen sperren
          </button>
          <button
            type="button"
            onClick={openAddDayModal}
            data-testid="add-meal-plan-day-button"
            className="px-3 py-1.5 text-sm rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-900 border border-blue-300"
            title="Legt einen neuen Tag im Verlauf an, damit du Varianten und Preise im Voraus pflegen kannst."
          >
            + Tag hinzufügen
          </button>
          <button
            type="button"
            onClick={resetTodayShift}
            data-testid="reset-today-shift-button"
            className="px-3 py-1.5 text-sm rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300"
            title="Löscht alle Bestellungen des heutigen Tages."
          >
            Heutigen Tag zurücksetzen
          </button>
        </div>
      </div>

      <MealOrderingStatusModal
        isOpen={showMealStatusModal}
        departmentId={currentDepartment.department_id}
        requiresPassword={false}
        onClose={() => setShowMealStatusModal(false)}
      />

      {breakfastHistory.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>Keine Frühstücks-Bestellungen in den letzten 30 Tagen gefunden.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Daily History List with Pagination */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b">
              <div className="flex justify-between items-center">
                <h4 className="font-semibold text-gray-800">Tägliche Übersichten</h4>
                <span className="text-sm text-gray-600">
                  Seite {currentPage} von {totalPages} ({breakfastHistory.length} Einträge gesamt)
                </span>
              </div>
            </div>
            <div className="divide-y divide-gray-200">
              {currentItems.map((day, index) => (
                <div
                  key={day.date}
                  className={`p-6 hover:bg-gray-50 ${
                    selectedDate === day.date ? 'bg-blue-50' : ''
                  }`}
                >
                  {/* Day Summary Header */}
                  <div className="flex flex-col gap-4">
                    {/* Top Row: Date and Stats */}
                    <div className="flex items-center justify-between">
                      <div 
                        className="flex-1 cursor-pointer"
                        onClick={() => setSelectedDate(selectedDate === day.date ? null : day.date)}
                      >
                        <h5 className="font-semibold text-lg text-gray-900">{formatDate(day.date)}</h5>
                        <p className="text-sm text-gray-500 mt-1">
                          {day.total_orders} Bestellungen • {day.total_amount.toFixed(2)} €
                          {(() => {
                            const mealSummary = buildMealHistorySummary(day.employee_orders);
                            return mealSummary.length > 0 ? ` • ${mealSummary.join(' • ')}` : '';
                          })()}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {isNightOnlyDateEditable(day.date, getBerlinDate()) ? (
                          <button
                            type="button"
                            onClick={() => updateNightOnly(day.date, !day.night_only)}
                            disabled={nightOnlySavingDate === day.date}
                            aria-pressed={Boolean(day.night_only)}
                            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                              day.night_only
                                ? 'border-indigo-300 bg-indigo-100 text-indigo-800'
                                : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-100'
                            } disabled:cursor-wait disabled:opacity-60`}
                            data-testid={`night-only-toggle-${day.date}`}
                          >
                            {nightOnlySavingDate === day.date
                              ? 'Speichern …'
                              : (day.night_only ? 'Nur Nachtdienst aktiv' : 'Nur Nachtdienst')}
                          </button>
                        ) : day.night_only ? (
                          <span className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700">
                            Nur Nachtdienst
                          </span>
                        ) : null}

                        {/* Expand/Collapse Indicator */}
                        <button
                          onClick={() => setSelectedDate(selectedDate === day.date ? null : day.date)}
                          className="px-3 py-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        >
                          {selectedDate === day.date ? '▲' : '▼'}
                        </button>
                      </div>
                    </div>

                    {/* Info Cards Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      {/* Brötchen Info Card */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1">Brötchen</div>
                        <div className="text-sm font-medium text-gray-900">
                          {day.shopping_list.weiss?.whole_rolls || 0} Helle • {day.shopping_list.koerner?.whole_rolls || 0} Körner
                        </div>
                      </div>

                      {/* Lunch Price Card */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1">Mittagessen</div>
                        <div className="space-y-3">
                          <div className="min-w-0">
                            {(() => {
                              // Prefer variants (with real prices) over daily_lunch_price fallback
                              const variants = (day.lunch_variants || []).filter(v => v && v.id !== 'default' && (v.price || 0) > 0);
                              if (variants.length > 0) {
                                const prices = variants.map(v => v.price);
                                const minP = Math.min(...prices);
                                const maxP = Math.max(...prices);
                                return (
                                  <>
                                    <div className="text-sm font-semibold text-gray-900">
                                      {minP === maxP
                                        ? `${minP.toFixed(2)} €`
                                        : `${minP.toFixed(2)} – ${maxP.toFixed(2)} €`}
                                    </div>
                                    <div className="text-[10px] text-violet-700 mt-0.5 space-y-0.5 break-words">
                                      {variants.map(v => (
                                        <div key={v.id}>
                                          {v.name || 'Variante'}: {(v.price || 0).toFixed(2)} €
                                        </div>
                                      ))}
                                    </div>
                                  </>
                                );
                              }
                              return (
                                <>
                                  <div className="text-sm font-semibold text-gray-900">
                                    {day.daily_lunch_price > 0
                                      ? `${day.daily_lunch_price.toFixed(2)} €`
                                      : (defaultLunchPrice && defaultLunchPrice > 0
                                          ? `${defaultLunchPrice.toFixed(2)} €`
                                          : 'Nicht gesetzt')}
                                  </div>
                                  {day.lunch_name && (
                                    <div className="text-xs text-gray-500 mt-0.5 break-words">
                                      {day.lunch_name}
                                    </div>
                                  )}
                                  {!(day.daily_lunch_price > 0) && defaultLunchPrice && defaultLunchPrice > 0 && (
                                    <div
                                      className="text-[10px] text-blue-600 mt-0.5 italic"
                                      data-testid="lunch-fallback-indicator"
                                    >
                                      Pauschalpreis
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openVariantsModal(day.date, 'lunch');
                            }}
                            data-testid="lunch-pricing-button"
                            className="w-full px-3 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors text-xs sm:text-sm font-medium"
                          >
                            Preis festlegen
                          </button>
                        </div>
                      </div>

                      {/* Dinner Price Card (Phase 4) */}
                      {dinnerEnabled && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1">Abendessen</div>
                        <div className="space-y-3">
                          <div className="min-w-0">
                            {(() => {
                              const variants = (day.dinner_variants || []).filter(v => v && v.id !== 'default' && (v.price || 0) > 0);
                              if (variants.length > 0) {
                                const prices = variants.map(v => v.price);
                                const minP = Math.min(...prices);
                                const maxP = Math.max(...prices);
                                return (
                                  <>
                                    <div className="text-sm font-semibold text-gray-900">
                                      {minP === maxP
                                        ? `${minP.toFixed(2)} €`
                                        : `${minP.toFixed(2)} – ${maxP.toFixed(2)} €`}
                                    </div>
                                    <div className="text-[10px] text-indigo-700 mt-0.5 space-y-0.5 break-words">
                                      {variants.map(v => (
                                        <div key={v.id}>
                                          {v.name || 'Variante'}: {(v.price || 0).toFixed(2)} €
                                        </div>
                                      ))}
                                    </div>
                                  </>
                                );
                              }
                              return (
                                <>
                                  <div className="text-sm font-semibold text-gray-900">
                                    {day.daily_dinner_price > 0
                                      ? `${day.daily_dinner_price.toFixed(2)} €`
                                      : (defaultDinnerPrice && defaultDinnerPrice > 0
                                          ? `${defaultDinnerPrice.toFixed(2)} €`
                                          : 'Nicht gesetzt')}
                                  </div>
                                  {day.dinner_name && (
                                    <div className="text-xs text-gray-500 mt-0.5 break-words">
                                      {day.dinner_name}
                                    </div>
                                  )}
                                  {!(day.daily_dinner_price > 0) && defaultDinnerPrice && defaultDinnerPrice > 0 && (
                                    <div
                                      className="text-[10px] text-indigo-600 mt-0.5 italic"
                                      data-testid="dinner-fallback-indicator"
                                    >
                                      Pauschalpreis
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openVariantsModal(day.date, 'dinner');
                            }}
                            data-testid="dinner-pricing-button"
                            className="w-full px-3 py-2 bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg transition-colors text-xs sm:text-sm font-medium"
                          >
                            Preis festlegen
                          </button>
                        </div>
                      </div>
                      )}

                      {/* Sponsoring Actions Card */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-2">Ausgeben</div>
                        {Object.entries(dailySponsorStatus[day.date]?.pending_sponsoring || {}).map(([meal, pending]) => (
                          <div key={meal} className="mb-2 text-sm text-amber-800" role="status">
                            {{ breakfast: 'Frühstück', lunch: 'Mittagessen', dinner: 'Abendessen' }[meal]}: {pending.status === 'reversing' ? 'Rücknahme noch offen – bitte im Developerbereich abschließen.' : 'Sponsoring noch nicht abgeschlossen.'}
                            {pending.status === 'applying' && (
                              <button type="button" className="ml-2 underline font-medium" onClick={(event) => {
                                event.stopPropagation();
                                handleSponsorMeal({ id: pending.sponsor_id, name: pending.sponsor_name }, meal, day.date);
                              }}>Fortsetzen</button>
                            )}
                          </div>
                        ))}
                        <div className="flex flex-wrap gap-2">
                          {/* Breakfast Sponsor Button */}
                          {dailySponsorStatus[day.date]?.breakfast_sponsored ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-md whitespace-nowrap" title={`Frühstück bereits ausgegeben von: ${dailySponsorStatus[day.date].breakfast_sponsored.sponsored_by}`}>
                              <span>Frühstück</span>
                              <span className="text-green-600">✓</span>
                            </span>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSponsorModalData({ mealType: 'breakfast', date: day.date });
                                setShowSponsorModal(true);
                              }}
                              className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-100 hover:border-gray-400 transition-colors whitespace-nowrap"
                              title="Frühstück ausgeben lassen"
                            >
                              Frühstück
                            </button>
                          )}
                          
                          {/* Lunch Sponsor Button */}
                          {dailySponsorStatus[day.date]?.lunch_sponsored ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-md whitespace-nowrap" title={`Mittagessen bereits ausgegeben von: ${dailySponsorStatus[day.date].lunch_sponsored.sponsored_by}`}>
                              <span>Mittag</span>
                              <span className="text-green-600">✓</span>
                            </span>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSponsorModalData({ mealType: 'lunch', date: day.date });
                                setShowSponsorModal(true);
                              }}
                              className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-100 hover:border-gray-400 transition-colors whitespace-nowrap"
                              title="Mittagessen ausgeben lassen"
                            >
                              Mittag
                            </button>
                          )}

                          {/* Dinner Sponsor Button (Phase 4) */}
                          {(() => {
                            const dinnerPortions = Object.values(day.employee_orders || {}).reduce((s, e) => s + (e.has_dinner ? (e.dinner_count || 1) : 0), 0);
                            if (dinnerPortions <= 0) return null;
                            const dinnerSponsored = dailySponsorStatus[day.date]?.dinner_sponsored;
                            if (dinnerSponsored) {
                              return (
                                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-md whitespace-nowrap" title={`Abendessen bereits ausgegeben von: ${dinnerSponsored.sponsored_by}`}>
                                  <span>Abend</span>
                                  <span className="text-green-600">✓</span>
                                </span>
                              );
                            }
                            return (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSponsorModalData({ mealType: 'dinner', date: day.date });
                                  setShowSponsorModal(true);
                                }}
                                data-testid={`sponsor-dinner-${day.date}`}
                                className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-100 hover:border-gray-400 transition-colors whitespace-nowrap"
                                title="Abendessen ausgeben lassen"
                              >
                                Abend
                              </button>
                            );
                          })()}
                        </div>
                      </div>


                      {/* Actions Card */}
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-2">Aktionen</div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteBreakfastDay(day.date);
                          }}
                          disabled={deleting === day.date}
                          className={`w-full px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                            deleting === day.date 
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                              : 'bg-white border border-red-300 text-red-600 hover:bg-red-50'
                          }`}
                          title="Tag löschen"
                        >
                          {deleting === day.date ? 'Wird gelöscht...' : '🗑️ Löschen'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Detailed View */}
                  {selectedDate === day.date && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      {/* Shopping List */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h6 className="font-semibold text-blue-800 mb-3">Einkaufsliste</h6>
                          <div className="space-y-2 text-sm">
                            {day.shopping_list.weiss && (
                              <div className="flex justify-between">
                                <span>Helle Brötchen:</span>
                                <span className="font-medium">{day.shopping_list.weiss.whole_rolls} Stück</span>
                              </div>
                            )}
                            {day.shopping_list.koerner && (
                              <div className="flex justify-between">
                                <span>Körnerbrötchen:</span>
                                <span className="font-medium">{day.shopping_list.koerner.whole_rolls} Stück</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <h6 className="font-semibold text-green-800 mb-3">Tagesstatistik</h6>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span>Bestellungen:</span>
                              <span className="font-medium">{day.total_orders}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Umsatz Frühstück:</span>
                              <span className="font-medium">
                                {dailyRevenues[day.date]?.breakfast_revenue?.toFixed(2) || '0.00'} €
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Umsatz Mittagessen:</span>
                              <span className="font-medium">
                                {dailyRevenues[day.date]?.lunch_revenue?.toFixed(2) || '0.00'} €
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Employee Details */}
                      <div>
                        <h6 className="font-semibold text-gray-800 mb-3">Mitarbeiter Bestellungen</h6>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {Object.entries(day.employee_orders).map(([employeeName, employeeData]) => (
                            <div key={employeeName} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                                <h6 className="font-medium text-gray-800">{employeeName}</h6>
                                {/* NEU: Gastmitarbeiter und 8H-Mitarbeiter Marker */}
                                {(() => {
                                  // Check if 8H-Service employee
                                  if (employeeData.is_8h_service) {
                                    return (
                                      <span className="inline-block bg-orange-100 text-orange-800 text-xs font-semibold px-2 py-1 rounded mt-1 sm:mt-0">
                                        Übergreifend
                                      </span>
                                    );
                                  }
                                  
                                  // Check if guest from another department
                                  if (employeeData.employee_department_id && 
                                      employeeData.order_department_id && 
                                      employeeData.employee_department_id !== employeeData.order_department_id) {
                                    return (
                                      <span className="inline-block bg-purple-100 text-purple-800 text-xs font-semibold px-2 py-1 rounded mt-1 sm:mt-0">
                                        👥 Gast aus {(() => {
                                          // Dynamic Department-Name-Mapping (supports any wache_nummer)
                                          const match = employeeData.employee_department_id.match(/abteilung(\d+)$/);
                                          const deptName = match ? `${match[1]}. WA` : employeeData.employee_department_id;
                                          return deptName;
                                        })()}
                                      </span>
                                    );
                                  }
                                  
                                  return null;
                                })()}
                              </div>
                              <div className="mt-2 space-y-1 text-sm text-gray-600">
                                <div>Helle Hälften: {employeeData.white_halves}</div>
                                <div>Körner Hälften: {employeeData.seeded_halves}</div>
                                {employeeData.boiled_eggs > 0 && (
                                  <div>🥚 Gekochte Eier: {employeeData.boiled_eggs}</div>
                                )}
                                {employeeData.fried_eggs > 0 && (
                                  <div>🍳 Spiegeleier: {employeeData.fried_eggs}</div>
                                )}
                                {employeeData.has_coffee && (
                                  <div>☕ Kaffee: Ja</div>
                                )}
                                {employeeData.has_lunch && (
                                  <div>
                                    🍽️ Mittagessen: {(employeeData.lunch_count || 1) > 1 ? `${employeeData.lunch_count} Portionen` : 'Ja'}
                                    {(() => {
                                      const vars = employeeData.lunch_variants || {};
                                      const entries = Object.entries(vars).filter(([, c]) => (c || 0) > 0);
                                      if (entries.length === 0) return null;
                                      if (entries.length === 1 && entries[0][0] === 'Mittagessen') return null;
                                      return (
                                        <span className="ml-1 text-xs text-violet-600">
                                          ({entries.map(([n, c]) => `${c}× ${n}`).join(', ')})
                                        </span>
                                      );
                                    })()}
                                  </div>
                                )}
                                {employeeData.has_dinner && (
                                  <div>
                                    🌙 Abendessen: {(employeeData.dinner_count || 1) > 1 ? `${employeeData.dinner_count} Portionen` : 'Ja'}
                                    {(() => {
                                      const vars = employeeData.dinner_variants || {};
                                      const entries = Object.entries(vars).filter(([, c]) => (c || 0) > 0);
                                      if (entries.length === 0) return null;
                                      if (entries.length === 1 && entries[0][0] === 'Abendessen') return null;
                                      return (
                                        <span className="ml-1 text-xs text-indigo-600">
                                          ({entries.map(([n, c]) => `${c}× ${n}`).join(', ')})
                                        </span>
                                      );
                                    })()}
                                  </div>
                                )}
                                <div className="pt-1 border-t">
                                  <strong>Total: {employeeData.total_amount.toFixed(2)} €</strong>
                                </div>
                                
                                {/* Sponsoring Information */}
                                {(employeeData.sponsored_breakfast || employeeData.sponsored_lunch) && (
                                  <div className="pt-2 border-t border-green-200 bg-green-50 rounded p-2 mt-2">
                                    <div className="text-green-700 font-medium text-xs mb-1">🎁 Sponsoring:</div>
                                    {employeeData.sponsored_breakfast && (
                                      <div className="text-green-600 text-xs">
                                        Hat {employeeData.sponsored_breakfast.count}x Frühstück ausgegeben für {employeeData.sponsored_breakfast.amount.toFixed(2)} €
                                      </div>
                                    )}
                                    {employeeData.sponsored_lunch && (
                                      <div className="text-green-600 text-xs">
                                        Hat {employeeData.sponsored_lunch.count}x Mittagessen ausgegeben für {employeeData.sponsored_lunch.amount.toFixed(2)} €
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {Object.keys(employeeData.toppings).length > 0 && (
                                  <div className="pt-1 text-xs">
                                    Beläge: {Object.entries(employeeData.toppings).map(([topping, count]) => {
                                      // Handle both string and object toppings
                                      const toppingName = typeof topping === 'string' ? topping : (topping.name || topping.topping_type || 'Unknown');
                                      
                                      // Handle new format: count = {white: X, seeded: Y}
                                      if (typeof count === 'object' && count !== null) {
                                        const totalCount = (count.white || 0) + (count.seeded || 0);
                                        const preparation = butterPreparationLabel(count);
                                        return `${totalCount}x ${toppingName}${preparation ? ` (ohne Butter: ${preparation})` : ''}`;
                                      } else {
                                        // Handle old format: count = number
                                        return `${count}x ${toppingName}`;
                                      }
                                    }).join(', ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Meal Sponsor Modal */}
      <MealSponsorModal
        isOpen={showSponsorModal}
        onClose={() => setShowSponsorModal(false)}
        employees={departmentEmployees}
        mealType={sponsorModalData.mealType}
        date={sponsorModalData.date}
        departmentId={currentDepartment.department_id}
        initialSponsor={sponsorModalData.initialSponsor}
        onCompleted={refreshAfterSponsoring}
      />

      {/* Unified meal pricing modal */}
      {variantsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-4" onClick={() => setVariantsModal(null)}>
          <div
            className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid={variantsModal.meal_type === 'lunch' ? 'lunch-pricing-modal' : 'dinner-pricing-modal'}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-snug">
                {variantsModal.meal_type === 'lunch'
                  ? `Mittagessen-Preis festlegen für ${formatDate(variantsModal.date)}`
                  : `Abendessen-Preis festlegen für ${formatDate(variantsModal.date)}`}
              </h3>
              <button
                onClick={() => setVariantsModal(null)}
                className="shrink-0 text-gray-400 hover:text-gray-700 text-2xl leading-none p-1"
                aria-label="Dialog schließen"
              >
                ×
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Bei einem Eintrag gilt der Preis für jedes {variantsModal.meal_type === 'lunch' ? 'Mittagessen' : 'Abendessen'}.
              {' '}Bei einem Essen kannst du den Preis leer lassen und später festlegen; ein vorhandener Preis bleibt erhalten.
              {' '}Ab zwei Einträgen benötigen alle Varianten einen Preis.
            </p>
            <MealVariantAssignmentNotice
              initialVariantCount={variantsInitialCount}
              draftVariantCount={variantsDraft.length}
            />
            <div className="space-y-3">
              {variantsDraft.map((v, i) => (
                <div key={i} className="border border-gray-200 rounded-xl p-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-xs font-medium text-gray-600">
                      {variantsDraft.length === 1 ? 'Essen' : `Variante ${i + 1}`}
                    </span>
                    {variantsDraft.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setVariantsDraft(d => d.filter((_, j) => j !== i))}
                        className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                        title="Variante entfernen"
                        data-testid={`variant-remove-${i}`}
                      >
                        Entfernen
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_8rem] gap-3">
                    <label className="block min-w-0">
                      <span className="block text-xs text-gray-500 mb-1">Name</span>
                      <input
                        type="text"
                        value={v.name}
                        onChange={(e) => setVariantsDraft(d => d.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                        placeholder="z.B. Schnitzel"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                        data-testid={`variant-name-${i}`}
                      />
                    </label>
                    <label className="block">
                      <span className="block text-xs text-gray-500 mb-1">Preis</span>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={v.price}
                          onChange={(e) => setVariantsDraft(d => d.map((x, j) => j === i ? { ...x, price: e.target.value } : x))}
                          placeholder="0,00"
                          className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                          data-testid={`variant-price-${i}`}
                        />
                        <span className="text-gray-500 text-sm">€</span>
                      </div>
                    </label>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setVariantsDraft(d => [...d, { name: '', price: '' }])}
                data-testid="add-variant-button"
                className="w-full py-2 border-2 border-dashed border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 text-sm"
              >
                + Variante hinzufügen
              </button>
            </div>
            <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button onClick={() => setVariantsModal(null)} className="w-full sm:w-auto px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">Abbrechen</button>
              <button
                onClick={saveVariants}
                disabled={variantsSaving}
                data-testid="save-variants-button"
                className="w-full sm:w-auto px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400"
              >
                {variantsSaving ? 'Speichern…' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ERWEITERT: Other Departments Tab (Andere WA)
export const OtherDepartmentsTab = ({allowLocal = true, ...props}) => {
  const [source, setSource] = useState(allowLocal ? 'local' : 'external');
  const activeSource = allowLocal ? source : 'external';
  return <div>
    <h2 className="mb-5 text-2xl font-semibold text-gray-800">{allowLocal ? 'Andere Wachabteilungen' : 'Andere Wachen'}</h2>
    {allowLocal && <div className="mb-6 inline-flex gap-1 rounded-xl bg-gray-100 p-1" aria-label="Herkunft auswählen">
      {[['local','Eigene Wache'],['external','Andere Wachen']].map(([value,label]) => <button key={value} type="button" aria-pressed={activeSource === value} onClick={() => setSource(value)} className={`rounded-lg px-4 py-2 text-sm font-medium ${activeSource === value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}>{label}</button>)}
    </div>}
    <DepartmentGuestAccounts key={`${props.currentDepartment?.department_id}-${activeSource}`} {...props} source={activeSource}/>
  </div>;
};

const DepartmentGuestAccounts = ({ source = 'local', currentDepartment, setPaymentEmployeeData, setShowPaymentModal, onBalanceUpdated, departmentConfig = { use_combined_accounts: false } }) => {
  const [otherEmployeesWithBalances, setOtherEmployeesWithBalances] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeProfile, setShowEmployeeProfile] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [failedCursor, setFailedCursor] = useState('');
  const requestRef = useRef(null);
  useEffect(() => () => requestRef.current?.abort(), []);
  const [selectedStation,setSelectedStation]=useState('');
  const [selectedDepartment,setSelectedDepartment]=useState('');
  const money=value=>`${Number(value||0).toLocaleString('de-DE',{minimumFractionDigits:2,maximumFractionDigits:2})} €`;
  const totalFor=rows=>rows.reduce((sum,employee)=>sum+(employee.use_combined_accounts?Math.round((employee.subaccount_balance?.combined||0)*100):Math.round((employee.subaccount_balance?.breakfast||0)*100)+Math.round((employee.subaccount_balance?.drinks||0)*100)),0)/100;
  const stationFor=employee=>source==='external'?employee.external_origin.station_id:'local';
  const departmentFor=employee=>source==='external'?employee.external_origin.department_id:(employee.department_id||employee.main_department_name||'unknown');
  const departmentLabel=employee=>source==='external'?externalGuestDisplay(employee).department:employee.main_department_name||'Unbekannte Abteilung';
  const choice=(label,rows,onClick,key)=> <button key={key} type="button" aria-label={label} onClick={onClick} className="flex w-full items-center justify-between gap-4 rounded-xl bg-gray-50 p-5 text-left transition-colors hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gray-700"><span><span className="block text-base font-semibold text-gray-900">{label}</span><span className="mt-1 block text-sm text-gray-500">{rows.length} Mitarbeiter</span></span><span className="flex items-center gap-4"><span className="text-right"><span className="block text-xs text-gray-500">Gesamtsaldo</span><span className={`mt-1 block text-xl font-semibold tabular-nums ${totalFor(rows)<0?'text-red-700':'text-gray-900'}`}>{money(totalFor(rows))}</span></span><span aria-hidden="true">→</span></span></button>;

  useEffect(() => {
    if (currentDepartment?.department_id) {
      fetchOtherEmployeesWithBalances();
    }
  }, [currentDepartment]);

  // Add automatic refresh when component becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && currentDepartment?.department_id) {
        console.log('OtherDepartmentsTab: Visibility changed, refreshing data');
        fetchOtherEmployeesWithBalances();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentDepartment]);

  const fetchOtherEmployeesWithBalances = async (next = '') => {
    if (source === 'external') {
      requestRef.current?.abort();
      const controller = new AbortController(); requestRef.current = controller;
      setIsLoading(true); setLoadError(''); setFailedCursor(next);
      try {
        const guests=[];let pageCursor='';const seen=new Set();
        do {
          const response=await axios.get(`${API}/department-admin/${currentDepartment.department_id}/external-guests`,{signal:controller.signal,params:{mode:'all',cursor:pageCursor}});
          if(controller.signal.aborted)return;
          guests.push(...(response.data.items||[]));
          pageCursor=response.data.next_cursor||'';
          if(pageCursor&&seen.has(pageCursor))throw new Error('Repeated guest cursor');
          seen.add(pageCursor);
        }while(pageCursor);
        const rows = [...new Map(guests.map(guest=>[guest.id,guest])).values()].map(guest => {
          const employee = {id:guest.id, name:guest.name, department_id:'none', is_guest:true, is_external_guest:true,
            external_origin:guest.origin, external_department_name:guest.department_name, use_combined_accounts:guest.account.use_combined,
            subaccount_balance:{combined:guest.account.combined_cents/100 || 0, breakfast:guest.account.breakfast_cents/100 || 0, drinks:guest.account.drinks_cents/100 || 0}};
          const origin = externalGuestDisplay(employee);
          return {...employee, main_department_name:`Wache ${guest.origin.station_id} · ${origin.department}`};
        });
        setOtherEmployeesWithBalances(rows);
        setCursor(null);
      } catch (error) {
        if (!controller.signal.aborted) setLoadError('Gastkonten konnten nicht geladen werden.');
      } finally {if (!controller.signal.aborted) setIsLoading(false);}
      return;
    }
    try {
      setIsLoading(true);
      
      // OPTIMIERT: Eine einzige API-Anfrage für alle Mitarbeiter mit Subkonto-Balances
      const response = await axios.get(`${API}/departments/${currentDepartment.department_id}/employees-with-subaccount-balances`);
      setOtherEmployeesWithBalances(response.data);
      
    } catch (error) {
      console.error('Fehler beim Laden der anderen Mitarbeiter:', error);
      
      // FALLBACK: Alte Methode falls neuer Endpoint nicht verfügbar
      try {
        // Alle Mitarbeiter anderer Abteilungen holen
        const response = await axios.get(`${API}/departments/${currentDepartment.department_id}/other-employees`);
        const otherEmployeesByDept = response.data;
        
        // Für jeden Mitarbeiter die Subkonto-Balance in der aktuellen Abteilung prüfen
        const employeesWithBalances = [];
        
        for (const [deptId, employees] of Object.entries(otherEmployeesByDept)) {
          for (const employee of employees) {
            try {
              // Hole alle Balances des Mitarbeiters
              const balanceResponse = await axios.get(`${API}/employees/${employee.id}/all-balances`);
              const allBalances = balanceResponse.data;
              
              // Prüfe ob Mitarbeiter Subkonto-Balance in aktueller Abteilung hat
              const currentDeptBalance = allBalances.subaccount_balances[currentDepartment.department_id];
              
              if (currentDeptBalance && (currentDeptBalance.breakfast !== 0 || currentDeptBalance.drinks !== 0)) {
                employeesWithBalances.push({
                  ...employee,
                  subaccount_balance: currentDeptBalance,
                  main_department_name: allBalances.main_department_name
                });
              }
            } catch (error) {
              console.error(`Fehler beim Laden der Balance für ${employee.name}:`, error);
            }
          }
        }
        
        setOtherEmployeesWithBalances(employeesWithBalances);
      } catch (fallbackError) {
        console.error('Auch Fallback-Methode fehlgeschlagen:', fallbackError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    setShowEmployeeProfile(true);
  };

  const handleBalanceManagement = (employee) => {
    setSelectedEmployee(employee);
    // Erstelle ein erweiterte Balance-Management Modal das zwischen den Kontotypen unterscheidet
    setPaymentEmployeeData({
      employee: employee,
      paymentType: employee.use_combined_accounts ? 'combined' : null,
      accountLabel: employee.is_external_guest ? `Wache ${employee.external_origin.station_id}` : 'Subkonto-Verwaltung',
      isSubaccount: true,
      needsAccountTypeSelection: !employee.use_combined_accounts,
      onBalanceUpdated: handleBalanceUpdated // Custom callback for "Andere WA"
    });
    setShowPaymentModal(true);
  };

  const handleBalanceUpdated = async () => {
    console.log('OtherDepartmentsTab: handleBalanceUpdated called');
    
    // Refresh employee list after balance update
    await fetchOtherEmployeesWithBalances();
    
    // Also call the parent callback if provided
    if (onBalanceUpdated && typeof onBalanceUpdated === 'function') {
      onBalanceUpdated();
    }
    
    // Force a second refresh after a short delay to ensure backend changes are reflected
    setTimeout(async () => {
      console.log('OtherDepartmentsTab: Second refresh triggered');
      await fetchOtherEmployeesWithBalances();
    }, 1000);
  };

  if (isLoading && !otherEmployeesWithBalances.length) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p>Lade Mitarbeiter anderer Abteilungen...</p>
      </div>
    );
  }

  return (
    <div>
      {loadError && <div role="alert" className="mb-4 rounded-xl bg-red-50 p-4 text-sm text-red-800">{loadError}<button className="ml-3 font-semibold underline" onClick={() => fetchOtherEmployeesWithBalances(failedCursor)}>Erneut versuchen</button></div>}

      {otherEmployeesWithBalances.length === 0 && !loadError ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">👥</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Keine Gastbuchungen vorhanden</h3>
          <p className="text-gray-500">
            Hier erscheinen Mitarbeiter mit Gastbuchungen in {currentDepartment.department_name}.
          </p>
        </div>
      ) : (() => {
        const stationIds=[...new Set(otherEmployeesWithBalances.map(stationFor))].sort((a,b)=>a.localeCompare(b,'de',{numeric:true}));
        if(!selectedStation)return <div className="space-y-3">{stationIds.map(id=>choice(id==='local'?'Eigene Wache':`Wache ${id}`,otherEmployeesWithBalances.filter(employee=>stationFor(employee)===id),()=>setSelectedStation(id),id))}</div>;
        const stationEmployees=otherEmployeesWithBalances.filter(employee=>stationFor(employee)===selectedStation);
        const stationTitle=selectedStation==='local'?'Eigene Wache':`Wache ${selectedStation}`;
        const back=<button type="button" className="mb-4 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100" onClick={()=>selectedDepartment?setSelectedDepartment(''):setSelectedStation('')}>← Zurück</button>;
        const summary=<div className="mb-5 flex items-center justify-between gap-4"><h3 className="text-lg font-semibold text-gray-900">{stationTitle}</h3><div className="text-right"><p className="text-xs text-gray-500">Gesamtsaldo der Wache</p><p className="text-xl font-semibold tabular-nums">{money(totalFor(stationEmployees))}</p></div></div>;
        if(!selectedDepartment){
          const ids=[...new Set(stationEmployees.map(departmentFor))].sort();
          return <div>{back}{summary}<div className="space-y-3">{ids.map(id=>{const rows=stationEmployees.filter(employee=>departmentFor(employee)===id);return choice(departmentLabel(rows[0]),rows,()=>setSelectedDepartment(id),id);})}</div></div>;
        }
        // Only show employees after selecting a station and department.

        const employeesByDepartment = stationEmployees.filter(employee=>departmentFor(employee)===selectedDepartment).reduce((acc, employee) => {
          const deptName = departmentLabel(employee);
          if (!acc[deptName]) {
            acc[deptName] = [];
          }
          acc[deptName].push(employee);
          return acc;
        }, {});

        // Sortiere Abteilungen alphabetisch
        const sortedDepartments = Object.keys(employeesByDepartment).sort();

        return (
          <div className="space-y-3">{back}{summary}
            {sortedDepartments.map((departmentName) => {
              const employees = employeesByDepartment[departmentName];
              const isExpanded = true;
              const totalBalance = totalFor(employees);

              return (
                <section key={departmentName} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                  {/* Abteilungsheader */}
                  <div
                    className="flex w-full items-center justify-between gap-4 p-4 text-left transition-colors hover:bg-gray-50 sm:px-5"
                    data-testid={`other-department-toggle-${departmentName}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          aria-hidden="true"
                        >
                          ▼
                        </span>
                        <h3 className="truncate text-base font-semibold text-gray-800 sm:text-lg">{departmentName}</h3>
                      </div>
                      <p className="ml-5 mt-0.5 text-xs text-gray-500 sm:text-sm">
                        {employees.length} Mitarbeiter
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className={`text-base font-semibold sm:text-lg ${totalBalance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {money(totalBalance)}
                      </div>
                      <div className="text-xs text-gray-400">{cursor ? 'Saldo angezeigter Mitarbeiter' : 'Gesamtsaldo'}</div>
                    </div>
                  </div>

                  {/* Mitarbeiter-Grid für diese Abteilung */}
                  {isExpanded && (
                  <div className="grid grid-cols-1 gap-3 border-t border-gray-200 bg-gray-50/60 p-3 sm:grid-cols-2 sm:p-4 lg:grid-cols-3">
                    {employees
                      .sort((a, b) => {
                        // Berechne Total-Balance für beide Mitarbeiter
                        const getTotalBalance = (emp) => {
                          if (emp.use_combined_accounts) {
                            return Math.abs(emp.subaccount_balance?.combined || 0);
                          } else {
                            const breakfast = Math.abs(emp.subaccount_balance?.breakfast || 0);
                            const drinks = Math.abs(emp.subaccount_balance?.drinks || 0);
                            return breakfast + drinks;
                          }
                        };
                        
                        const balanceA = getTotalBalance(a);
                        const balanceB = getTotalBalance(b);
                        
                        // Mitarbeiter mit Saldo (nicht 0) zuerst, dann alphabetisch
                        if (balanceA > 0 && balanceB === 0) return -1;
                        if (balanceA === 0 && balanceB > 0) return 1;
                        
                        // Beide haben Saldo oder beide haben keinen: sortiere nach Balance (höchster zuerst)
                        if (balanceA !== balanceB) return balanceB - balanceA;
                        
                        // Bei gleichem Saldo: alphabetisch nach Name
                        return a.name.localeCompare(b.name);
                      })
                      .map((employee) => {
                      const isCombined = employee.use_combined_accounts;
                      const total = isCombined
                        ? (employee.subaccount_balance?.combined || 0)
                        : (employee.subaccount_balance?.breakfast || 0) + (employee.subaccount_balance?.drinks || 0);
                      
                      return (
                        <div
                          key={employee.id}
                          className="cursor-pointer rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
                          onClick={() => handleEmployeeClick(employee)}
                        >
                          <div className="mb-3 flex items-baseline justify-between gap-3">
                            <h3 className="min-w-0 flex-1 truncate text-base font-semibold text-gray-800">{employee.name}</h3>
                            <div className="shrink-0 text-right">
                              <div className={`text-base font-semibold ${total >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                {money(total)}
                              </div>
                              <div className="text-[11px] text-gray-400">Gesamt</div>
                            </div>
                          </div>
                          
                          {isCombined ? (
                            /* COMBINED ACCOUNTS: Zeige nur Gesamtsaldo */
                            <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-center">
                              <div className={`text-sm font-semibold ${(employee.subaccount_balance?.combined || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                {money(employee.subaccount_balance?.combined)}
                              </div>
                              <div className="text-xs text-gray-500">Gesamtsaldo</div>
                            </div>
                          ) : (
                            /* SEPARATE ACCOUNTS: Zeige getrennte Konten */
                            <div className="mb-3 grid grid-cols-2 gap-2">
                              <div className="rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-center">
                                <div className={`text-sm font-semibold ${(employee.subaccount_balance?.breakfast || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                  {money(employee.subaccount_balance?.breakfast)}
                                </div>
                                <div className="text-xs text-gray-500">Frühstück</div>
                              </div>
                              <div className="rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-center">
                                <div className={`text-sm font-semibold ${(employee.subaccount_balance?.drinks || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                  {money(employee.subaccount_balance?.drinks)}
                                </div>
                                <div className="text-xs text-gray-500">Getränke</div>
                              </div>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-2 gap-2 border-t border-gray-100 pt-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEmployeeClick(employee);
                              }}
                              className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
                            >
                              Verlauf anzeigen
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBalanceManagement(employee);
                              }}
                              className="rounded-lg bg-gray-800 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-900"
                            >
                              Saldo verwalten
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  )}
                </section>
              );
            })}
          </div>
        );
      })()}

      {source === 'external' && cursor && <button type="button" disabled={isLoading} className="mt-5 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50" onClick={() => fetchOtherEmployeesWithBalances(cursor)}>{isLoading ? 'Wird geladen …' : 'Weitere Mitarbeiter'}</button>}

      {/* Employee Profile Modal */}
      {showEmployeeProfile && selectedEmployee && (
        <IndividualEmployeeProfile
          employee={selectedEmployee}
          onClose={() => {
            setShowEmployeeProfile(false);
            setSelectedEmployee(null);
          }}
        />
      )}
    </div>
  );
};

// Statistics Tab Component
export const StatisticsTab = (props) => {
  const [activeTab, setActiveTab] = useState('balances');
  return <div className="space-y-6">
    <div className="flex flex-wrap gap-2" role="tablist" aria-label="Statistikansicht">
      {[['balances', 'Aktuelle Salden'], ['consumption', 'Verbrauch & Artikel']].map(([id, label]) => (
        <button key={id} role="tab" aria-selected={activeTab === id}
          className={`px-4 py-2 rounded-lg text-sm font-medium border ${activeTab === id ? 'bg-gray-800 border-gray-800 text-white' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          onClick={() => setActiveTab(id)}>{label}</button>
      ))}
    </div>
    {activeTab === 'balances' ? <CurrentBalancesTab {...props} /> : <ConsumptionStatistics departmentId={props.currentDepartment?.department_id} />}
  </div>;
};

const CurrentBalancesTab = ({ employees, eightHourEmployees = [], categories = [], currentDepartment, departmentConfig = { use_combined_accounts: false } }) => {
  const [activeStatisticsFilter, setActiveStatisticsFilter] = useState('all');
  const [showOnlyNegative, setShowOnlyNegative] = useState(false);

  if (!employees) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Lade Statistiken...</p>
      </div>
    );
  }

  const bySortOrder = (a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name);
  const displaySections = buildEmployeeDisplaySections(categories, employees, eightHourEmployees)
    .map(group => ({
      ...group,
      employees: group.entries.sort((a, b) => bySortOrder(a.employee, b.employee)),
    }));
  const regularEntries = displaySections[0]?.employees || [];
  const categorizedGroups = displaySections.slice(1).filter(group => group.employees.length > 0);
  const useCombinedAccounts = !!departmentConfig.use_combined_accounts;
  const filterEntries = entries => filterStatisticsEntries(entries, useCombinedAccounts, showOnlyNegative);
  const visibleRegularEntries = filterEntries(regularEntries);
  const visibleCategorizedGroups = categorizedGroups.map(group => ({
    ...group,
    employees: filterEntries(group.employees),
  }));
  const statisticsFilterTabs = [
    { key: 'regular', label: 'Regulär', count: visibleRegularEntries.length },
    ...visibleCategorizedGroups.map(group => ({
      key: group.key,
      label: group.label,
      color: group.color,
      count: group.employees.length,
    })),
    {
      key: 'all',
      label: 'Alle',
      count: visibleRegularEntries.length
        + visibleCategorizedGroups.reduce((sum, group) => sum + group.employees.length, 0),
    },
  ].filter(tab => tab.key === 'all' || tab.count > 0 || tab.key === activeStatisticsFilter);
  const activeFilterEntries = activeStatisticsFilter === 'all'
    ? [
        ...visibleRegularEntries,
        ...visibleCategorizedGroups.flatMap(group => group.employees),
      ]
    : activeStatisticsFilter === 'regular'
      ? visibleRegularEntries
      : visibleCategorizedGroups.find(group => group.key === activeStatisticsFilter)?.employees || [];
  const filteredBalanceEmployees = activeFilterEntries
    .filter(entry => !entry.isCrossDepartment)
    .map(entry => entry.employee);
  const filteredCrossDepartmentEmployees = activeFilterEntries
    .filter(entry => entry.isCrossDepartment)
    .map(entry => entry.employee);
  const activeFilterLabel = statisticsFilterTabs.find(tab => tab.key === activeStatisticsFilter)?.label || 'Alle';

  const formatBalance = (balance) => {
    const numBalance = parseFloat(balance) || 0;
    const rounded = Math.round(numBalance * 100) / 100; // Round to 2 decimals
    return (rounded === -0 ? 0 : rounded).toFixed(2); // Avoid -0.00
  };

  const getBalanceColor = (balance) => {
    const numBalance = parseFloat(balance) || 0;
    if (numBalance > 0) return 'text-green-600 bg-green-50';
    if (numBalance < 0) return 'text-red-600 bg-red-50';
    return 'text-gray-600 bg-gray-50';
  };

  const EmployeeStatCard = ({ employee, accentColor = null }) => {
    const useCombined = departmentConfig.use_combined_accounts;
    
    return (
      <div
        key={employee.id}
        className={`bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow ${accentColor ? 'border-l-4' : ''}`}
        style={accentColor ? { borderLeftColor: accentColor } : undefined}
      >
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-gray-800 text-sm truncate flex-1">
              {employee.name}
            </h3>
          </div>
          
          {useCombined ? (
            /* COMBINED ACCOUNTS: Zeige nur ein Konto */
            <div className="flex justify-center text-xs">
              <div className="text-center px-4">
                <div className="text-gray-500 mb-1 text-xs">Gesamtsaldo</div>
                <div className={`font-bold text-base ${parseFloat(employee.combined_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatBalance(employee.combined_balance || 0)}€
                </div>
              </div>
            </div>
          ) : (
            /* SEPARATE ACCOUNTS: Zeige getrennte Konten wie bisher */
            <div className="flex justify-between text-xs">
              <div className="text-center flex-1 px-1">
                <div className="text-gray-500 mb-1 text-xs">F/M</div>
                <div className={`font-semibold text-xs ${parseFloat(employee.breakfast_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatBalance(employee.breakfast_balance)}€
                </div>
              </div>
              <div className="text-center flex-1 px-1">
                <div className="text-gray-500 mb-1 text-xs">G/S</div>
                <div className={`font-semibold text-xs ${parseFloat(employee.drinks_sweets_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatBalance(employee.drinks_sweets_balance)}€
                </div>
              </div>
              <div className="text-center flex-1 px-1">
                <div className="text-gray-500 mb-1 text-xs">Gesamt</div>
                <div className={`font-bold text-xs ${(parseFloat(employee.breakfast_balance || 0) + parseFloat(employee.drinks_sweets_balance || 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatBalance(parseFloat(employee.breakfast_balance || 0) + parseFloat(employee.drinks_sweets_balance || 0))}€
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // NEU: 8H Employee Card - zeigt nur Subkonto-Saldos für diese Wachabteilung
  const EightHourEmployeeStatCard = ({ employee, accentColor = '#9CA3AF' }) => {
    const useCombined = departmentConfig.use_combined_accounts;
    
    return (
      <div
        key={employee.id}
        className="bg-white border border-gray-200 rounded-lg p-3 border-l-4 hover:shadow-sm transition-shadow"
        style={{ borderLeftColor: accentColor }}
      >
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-gray-800 text-sm truncate flex-1">
              {employee.name}
            </h3>
          </div>
          
          {useCombined ? (
            /* COMBINED ACCOUNTS: Zeige nur Gesamtsaldo */
            <div className="flex justify-center text-xs">
              <div className="text-center px-4">
                <div className="text-gray-500 mb-1 text-xs">Gesamtsaldo</div>
                <div className={`font-bold text-base ${parseFloat(employee.subaccount_combined_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatBalance(employee.subaccount_combined_balance || 0)}€
                </div>
              </div>
            </div>
          ) : (
            /* SEPARATE ACCOUNTS: Zeige getrennte Konten */
            <div className="flex justify-between text-xs">
              <div className="text-center flex-1 px-1">
                <div className="text-gray-500 mb-1 text-xs">F/M</div>
                <div className={`font-semibold text-xs ${parseFloat(employee.subaccount_breakfast_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatBalance(employee.subaccount_breakfast_balance)}€
                </div>
              </div>
              <div className="text-center flex-1 px-1">
                <div className="text-gray-500 mb-1 text-xs">G/S</div>
                <div className={`font-semibold text-xs ${parseFloat(employee.subaccount_drinks_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatBalance(employee.subaccount_drinks_balance)}€
                </div>
              </div>
              <div className="text-center flex-1 px-1">
                <div className="text-gray-500 mb-1 text-xs">Gesamt</div>
                <div className={`font-bold text-xs ${(parseFloat(employee.subaccount_breakfast_balance || 0) + parseFloat(employee.subaccount_drinks_balance || 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatBalance(parseFloat(employee.subaccount_breakfast_balance || 0) + parseFloat(employee.subaccount_drinks_balance || 0))}€
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const StatSection = ({ label, color = '#6B7280', entries, plainEmployees = false, crossDepartment = false }) => {
    if (!entries || entries.length === 0) return null;

    return (
      <section className="mb-6">
        <div className="flex items-center gap-3 mb-3">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }}></span>
          <h4 className="text-sm font-semibold text-gray-700">{label}</h4>
          <span className="text-xs text-gray-400">({entries.length})</span>
          <div className="h-px flex-1 bg-gray-200"></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          {plainEmployees
            ? entries.map(employee => crossDepartment
              ? <EightHourEmployeeStatCard key={employee.id} employee={employee} accentColor={color} />
              : <EmployeeStatCard key={employee.id} employee={employee} accentColor={color} />
            )
            : entries.map(({ employee, isCrossDepartment }) => isCrossDepartment
              ? <EightHourEmployeeStatCard key={employee.id} employee={employee} accentColor={color} />
              : <EmployeeStatCard key={employee.id} employee={employee} accentColor={color} />
            )}
        </div>
      </section>
    );
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
        📊 Saldo-Statistiken - {currentDepartment.department_name}
      </h3>

      <div className="mb-6 flex flex-wrap gap-2" data-testid="statistics-category-filters">
        {statisticsFilterTabs.map(tab => {
          const isActive = activeStatisticsFilter === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveStatisticsFilter(tab.key)}
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                isActive
                  ? 'border-gray-800 bg-gray-800 text-white'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
              }`}
              data-testid={`statistics-filter-${tab.key}`}
            >
              {tab.color && (
                <span
                  className="h-2.5 w-2.5 rounded-full border border-black/10"
                  style={{ backgroundColor: tab.color }}
                />
              )}
              <span>{tab.label}</span>
              <span className={isActive ? 'text-gray-300' : 'text-gray-400'}>({tab.count})</span>
            </button>
          );
        })}
      </div>

      <label className="mb-6 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm hover:bg-gray-50">
        <input
          type="checkbox"
          checked={showOnlyNegative}
          onChange={(event) => setShowOnlyNegative(event.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
        />
        <span>Nur negative Salden</span>
      </label>

      {(activeStatisticsFilter === 'all' || activeStatisticsFilter === 'regular') && (
        <StatSection label="Regulär" entries={visibleRegularEntries} />
      )}
      {visibleCategorizedGroups
        .filter(group => activeStatisticsFilter === 'all' || activeStatisticsFilter === group.key)
        .map(group => (
        <StatSection
          key={group.key}
          label={group.label}
          color={group.color}
          entries={group.employees}
        />
      ))}
      {employees.length === 0 && (!eightHourEmployees || eightHourEmployees.length === 0) && (
        <div className="text-center py-8">
          <p className="text-gray-500">Keine Mitarbeiter gefunden.</p>
        </div>
      )}
      {showOnlyNegative
        && activeFilterEntries.length === 0
        && (employees.length > 0 || eightHourEmployees.length > 0) && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-center">
          <p className="text-sm text-gray-500">Keine negativen Salden in dieser Auswahl.</p>
        </div>
      )}

      {/* Gesamtsaldi */}
      {activeFilterEntries.length > 0 && (
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h4 className="text-md font-medium text-gray-800 mb-4">📊 Gesamt – {activeFilterLabel}</h4>
          
          {departmentConfig.use_combined_accounts ? (
            /* COMBINED ACCOUNTS: Zeige nur Gesamtsaldo */
            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm max-w-md mx-auto">
              <div className="text-center">
                <div className="text-gray-600 text-sm mb-2">Gesamt ({activeFilterLabel})</div>
                <div className="text-xs text-gray-500 mb-1">Offene Schulden</div>
                <div className="text-2xl font-bold text-red-600">
                  {formatBalance(
                    filteredBalanceEmployees.reduce((sum, emp) => {
                      const balance = parseFloat(emp.combined_balance || 0);
                      return sum + (balance < 0 ? balance : 0);
                    }, 0) +
                    filteredCrossDepartmentEmployees.reduce((sum, emp) => {
                      const balance = parseFloat(emp.subaccount_combined_balance || 0);
                      return sum + (balance < 0 ? balance : 0);
                    }, 0)
                  )}€
                </div>
              </div>
            </div>
          ) : (
            /* SEPARATE ACCOUNTS: Zeige getrennte Kategorien */
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Gesamtsaldo Essen - nur negative Werte (offene Schulden) */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="text-center">
                  <div className="text-gray-600 text-sm mb-2">Essen</div>
                  <div className="text-xs text-gray-500 mb-1">Offene Schulden</div>
                  <div className="text-2xl font-bold text-red-600">
                    {formatBalance(
                      filteredBalanceEmployees.reduce((sum, emp) => {
                        const balance = parseFloat(emp.breakfast_balance || 0);
                        return sum + (balance < 0 ? balance : 0);
                      }, 0) +
                      filteredCrossDepartmentEmployees.reduce((sum, emp) => {
                        const balance = parseFloat(emp.subaccount_breakfast_balance || 0);
                        return sum + (balance < 0 ? balance : 0);
                      }, 0)
                    )}€
                  </div>
                </div>
              </div>

              {/* Gesamtsaldo Süßigkeiten & Getränke - nur negative Werte (offene Schulden) */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="text-center">
                  <div className="text-gray-600 text-sm mb-2">Snacks & Getränke</div>
                  <div className="text-xs text-gray-500 mb-1">Offene Schulden</div>
                  <div className="text-2xl font-bold text-red-600">
                    {formatBalance(
                      filteredBalanceEmployees.reduce((sum, emp) => {
                        const balance = parseFloat(emp.drinks_sweets_balance || 0);
                        return sum + (balance < 0 ? balance : 0);
                      }, 0) +
                      filteredCrossDepartmentEmployees.reduce((sum, emp) => {
                        const balance = parseFloat(emp.subaccount_drinks_balance || 0);
                        return sum + (balance < 0 ? balance : 0);
                      }, 0)
                    )}€
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Admin Settings Tab Component
export const AdminSettingsTab = ({
  currentDepartment,
  departmentConfig = {},
  onUpdateDefaultLunchPrice,
  onUpdateDefaultDinnerPrice,
  onToggleDinnerEnabled,
  onUpdateOrganizationFeatures,
  organizationFeaturesDisabled = false,
}) => {
  const { isSingleDepartment, loading: stationModeLoading } = useStationMode();
  const [newEmployeePassword, setNewEmployeePassword] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [lunchPriceInput, setLunchPriceInput] = useState('');
  const [dinnerPriceInput, setDinnerPriceInput] = useState('');
  const [showLunchPriceEditor, setShowLunchPriceEditor] = useState(false);
  const [showDinnerPriceEditor, setShowDinnerPriceEditor] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(
    localStorage.getItem('canteenAudioEnabled') !== 'false'
  );
  // P2: Sponsoring-Konfiguration
  const [sponsoringConfig, setSponsoringConfig] = useState(null);
  const [savingSponsoringConfig, setSavingSponsoringConfig] = useState(false);
  const [sponsoringCategories, setSponsoringCategories] = useState([]);
  const [sponsoringLoadError, setSponsoringLoadError] = useState('');
  const [sponsoringReload, setSponsoringReload] = useState(0);
  const [legacyGuestMeals, setLegacyGuestMeals] = useState([]);
  // Dynamische Ei-Namen (überschreibbar in Menü & Preise)
  const [eggLabels, setEggLabels] = useState({
    boiled: 'Gekochte Eier',
    fried: 'Spiegeleier',
    scrambled: 'Rührei',
  });

  useEffect(() => {
    let active = true;
    setSponsoringConfig(null);
    setSponsoringCategories([]);
    setSponsoringLoadError('');
    setLegacyGuestMeals([]);
    const departmentId = currentDepartment.department_id;
    Promise.all([
      axios.get(`${API}/department-settings/${departmentId}/sponsoring-config`),
      axios.get(`${API}/departments/${departmentId}/categories`),
    ]).then(([settings, categories]) => {
      if (!active) return;
      if (!Array.isArray(categories.data)) throw new Error('Ungültige Kategorienliste');
      setSponsoringCategories(categories.data);
      // Ignore orphaned IDs left by an interrupted category deletion.
      const validIds = new Set(categories.data.map(category => category.id));
      const config = Object.fromEntries(Object.entries(settings.data.sponsoring_config).map(([meal, options]) => [
        meal, {...options, excluded_category_ids: (options.excluded_category_ids || []).filter(id => validIds.has(id))},
      ]));
      setSponsoringConfig(config);
      setLegacyGuestMeals(settings.data.legacy_guest_exclusion_meals || []);
    }).catch(() => {
      if (active) setSponsoringLoadError('Sponsoring-Einstellungen oder Kategorien konnten nicht geladen werden.');
    });
    fetchEggLabels();
    return () => { active = false; };
  }, [currentDepartment.department_id, sponsoringReload]);

  useEffect(() => {
    setLunchPriceInput(departmentConfig.default_lunch_price?.toString() || '');
    setShowLunchPriceEditor(!!departmentConfig.default_lunch_price);
  }, [departmentConfig.default_lunch_price]);

  useEffect(() => {
    setDinnerPriceInput(departmentConfig.default_dinner_price?.toString() || '');
    setShowDinnerPriceEditor(!!departmentConfig.default_dinner_price);
  }, [departmentConfig.default_dinner_price]);

  const fetchEggLabels = async () => {
    try {
      const r = await axios.get(`${API}/lunch-settings?department_id=${currentDepartment.department_id}`);
      setEggLabels({
        boiled: r.data.boiled_eggs_name || 'Gekochte Eier',
        fried: r.data.fried_eggs_name || 'Spiegeleier',
        scrambled: r.data.scrambled_eggs_name || 'Rührei',
      });
    } catch (_) { /* ignore, keep defaults */ }
  };

  const toggleSponsoringCategory = (meal, categoryId) => {
    setSponsoringConfig(prev => {
      const ids = prev?.[meal]?.excluded_category_ids || [];
      return {...prev, [meal]: {...prev?.[meal], excluded_category_ids:
        ids.includes(categoryId) ? ids.filter(id => id !== categoryId) : [...ids, categoryId]}};
    });
  };

  const toggleSponsoringOption = (meal, key) => {
    setSponsoringConfig(prev => ({
      ...prev,
      [meal]: { ...(prev?.[meal] || {}), [key]: !prev?.[meal]?.[key] },
    }));
  };

  const saveSponsoringConfig = async () => {
    if (!sponsoringConfig) return;
    try {
      setSavingSponsoringConfig(true);
      const res = await axios.put(
        `${API}/department-settings/${currentDepartment.department_id}/sponsoring-config`,
        sponsoringConfig
      );
      setSponsoringConfig(res.data.sponsoring_config);
      setLegacyGuestMeals([]);
      alert('Sponsoring-Einstellungen gespeichert.\n\nHinweis: Änderungen wirken nur auf zukünftige Sponsoring-Vorgänge — bereits gesponserte Bestellungen bleiben unverändert.');
    } catch (err) {
      console.error('Fehler beim Speichern der Sponsoring-Einstellungen:', err);
      alert(err.response?.data?.detail || 'Fehler beim Speichern der Sponsoring-Einstellungen');
    } finally {
      setSavingSponsoringConfig(false);
    }
  };

  const toggleAudio = () => {
    const newAudioState = !audioEnabled;
    setAudioEnabled(newAudioState);
    localStorage.setItem('canteenAudioEnabled', newAudioState.toString());
    
    // Play test sound if enabling
    if (newAudioState) {
      playSucessSound();
    }
  };

  const changeEmployeePassword = async () => {
    if (!newEmployeePassword) {
      alert('Bitte neues Mitarbeiter-Passwort eingeben');
      return;
    }

    if (newAdminPassword && newEmployeePassword === newAdminPassword) {
      alert('Mitarbeiter- und Admin-Passwort müssen unterschiedlich sein');
      return;
    }

    try {
      await axios.put(`${API}/department-admin/change-employee-password/${currentDepartment.department_id}?new_password=${newEmployeePassword}`);
      alert('Mitarbeiter-Passwort erfolgreich geändert');
      setNewEmployeePassword('');
    } catch (error) {
      console.error('Fehler beim Ändern des Mitarbeiter-Passworts:', error);
      alert('Fehler beim Ändern des Mitarbeiter-Passworts');
    }
  };

  const changeAdminPassword = async () => {
    if (!newAdminPassword) {
      alert('Bitte neues Admin-Passwort eingeben');
      return;
    }

    if (newEmployeePassword && newEmployeePassword === newAdminPassword) {
      alert('Mitarbeiter- und Admin-Passwort müssen unterschiedlich sein');
      return;
    }

    try {
      await axios.put(`${API}/department-admin/change-admin-password/${currentDepartment.department_id}?new_password=${newAdminPassword}`);
      alert('Admin-Passwort erfolgreich geändert');
      setNewAdminPassword('');
    } catch (error) {
      console.error('Fehler beim Ändern des Admin-Passworts:', error);
      alert('Fehler beim Ändern des Admin-Passworts');
    }
  };

  const dinnerIsEnabled = departmentConfig.meal_types?.dinner?.enabled !== false;

  return (
    <div>
      <div className="flex flex-col gap-8">
        <div className="order-1">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Mahlzeiten</h4>
            <p className="text-sm text-gray-500 mb-5">
              Pauschalpreise gelten automatisch, solange im Essensverlauf kein Tagespreis oder keine Variante festgelegt wurde.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {departmentConfig.is_breakfast_lunch_enabled && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-5">
                  <h5 className="font-semibold text-orange-900 mb-2">Mittagessen</h5>
                  <p className="text-sm text-gray-600 mb-4">Mittagessen ist für diese Abteilung dauerhaft aktiv.</p>
                  {!showLunchPriceEditor ? (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <span className="text-sm text-gray-600">Kein Pauschalpreis eingerichtet</span>
                      <button
                        type="button"
                        onClick={() => setShowLunchPriceEditor(true)}
                        className="w-full sm:w-auto px-4 py-2 bg-white border border-orange-300 text-orange-800 hover:bg-orange-100 rounded-lg text-sm font-medium"
                        data-testid="default-lunch-price-open"
                      >
                        Pauschalpreis festlegen
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={lunchPriceInput}
                            onChange={(e) => setLunchPriceInput(e.target.value)}
                            className="w-full sm:w-32 px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="0.00"
                            data-testid="default-lunch-price-input"
                          />
                          <span className="text-gray-500">€</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => onUpdateDefaultLunchPrice?.(parseFloat(lunchPriceInput) || 0)}
                          className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium"
                          data-testid="default-lunch-price-save"
                        >
                          Speichern
                        </button>
                        {!departmentConfig.default_lunch_price && (
                          <button
                            type="button"
                            onClick={() => {
                              setLunchPriceInput('');
                              setShowLunchPriceEditor(false);
                            }}
                            className="px-3 py-2 text-gray-600 hover:text-gray-900 text-sm"
                          >
                            Abbrechen
                          </button>
                        )}
                      </div>
                      {departmentConfig.default_lunch_price && (
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <span className="font-medium text-orange-800">
                            Aktuell: {departmentConfig.default_lunch_price.toFixed(2).replace('.', ',')} €
                          </span>
                          <button
                            type="button"
                            onClick={() => onUpdateDefaultLunchPrice?.(0)}
                            className="text-gray-500 hover:text-red-600"
                            data-testid="default-lunch-price-remove"
                          >
                            Pauschalpreis entfernen
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <div>
                    <h5 className="font-semibold text-indigo-900">Abendessen</h5>
                    <p className="text-sm text-gray-600 mt-1">Nachtdienst-Verpflegung für diese Abteilung.</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-medium ${dinnerIsEnabled ? 'text-green-700' : 'text-gray-500'}`}>
                      {dinnerIsEnabled ? 'Aktiviert' : 'Deaktiviert'}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={dinnerIsEnabled}
                      aria-label="Abendessen aktivieren"
                      onClick={() => onToggleDinnerEnabled?.(!dinnerIsEnabled)}
                      className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                        dinnerIsEnabled ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                      data-testid="dinner-enabled-toggle"
                    >
                      <span
                        className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                          dinnerIsEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
                {dinnerIsEnabled && (
                  !showDinnerPriceEditor ? (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <span className="text-sm text-gray-600">Kein Pauschalpreis eingerichtet</span>
                      <button
                        type="button"
                        onClick={() => setShowDinnerPriceEditor(true)}
                        className="w-full sm:w-auto px-4 py-2 bg-white border border-indigo-300 text-indigo-800 hover:bg-indigo-100 rounded-lg text-sm font-medium"
                        data-testid="default-dinner-price-open"
                      >
                        Pauschalpreis festlegen
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={dinnerPriceInput}
                            onChange={(e) => setDinnerPriceInput(e.target.value)}
                            className="w-full sm:w-32 px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="0.00"
                            data-testid="default-dinner-price-input"
                          />
                          <span className="text-gray-500">€</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => onUpdateDefaultDinnerPrice?.(parseFloat(dinnerPriceInput) || 0)}
                          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium"
                          data-testid="default-dinner-price-save"
                        >
                          Speichern
                        </button>
                        {!departmentConfig.default_dinner_price && (
                          <button
                            type="button"
                            onClick={() => {
                              setDinnerPriceInput('');
                              setShowDinnerPriceEditor(false);
                            }}
                            className="px-3 py-2 text-gray-600 hover:text-gray-900 text-sm"
                          >
                            Abbrechen
                          </button>
                        )}
                      </div>
                      {departmentConfig.default_dinner_price && (
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-medium text-indigo-800">
                          Aktuell: {departmentConfig.default_dinner_price.toFixed(2).replace('.', ',')} €
                        </span>
                        <button
                          type="button"
                          onClick={() => onUpdateDefaultDinnerPrice?.(0)}
                          className="text-gray-500 hover:text-red-600"
                          data-testid="default-dinner-price-remove"
                        >
                          Pauschalpreis entfernen
                        </button>
                      </div>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
        </div>

        <div className="order-2">
          <OrganizationFeatureSettings
            settings={departmentConfig}
            onSave={onUpdateOrganizationFeatures}
            disabled={organizationFeaturesDisabled}
          />
        </div>

        {/* Password Management */}
        <div className="order-4 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-1">Zugang & Sicherheit</h4>
          <p className="text-sm text-gray-600 mb-5">Passwörter und Zugänge dieser Wachabteilung verwalten.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">Neues Mitarbeiter-Passwort</label>
              <input
                type="password"
                value={newEmployeePassword}
                onChange={(e) => setNewEmployeePassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                placeholder="Neues Passwort für Mitarbeiter"
              />
              <div className="mt-2">
                <button
                  onClick={changeEmployeePassword}
                  disabled={!newEmployeePassword}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                >
                  Mitarbeiter-Passwort ändern
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Neues Admin-Passwort</label>
              <input
                type="password"
                value={newAdminPassword}
                onChange={(e) => setNewAdminPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                placeholder="Neues Passwort für Admin"
              />
              <div className="mt-2">
                <button
                  onClick={changeAdminPassword}
                  disabled={!newAdminPassword}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                >
                  Admin-Passwort ändern
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <p><strong>Hinweis:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Passwörter können unabhängig voneinander geändert werden</li>
              <li>Beide Passwörter müssen unterschiedlich sein</li>
              <li>Nach der Änderung müssen sich betroffene Benutzer neu anmelden</li>
              <li>Passwörter werden sofort für diese Wachabteilung geändert</li>
            </ul>
          </div>
        </div>

        {/* Sound Settings */}
        <div className="order-5">
          <h4 className="text-lg font-semibold text-gray-900 mb-1">Gerät & System</h4>
          <p className="text-sm text-gray-500">
            Lokale Darstellung sowie technische Informationen zu dieser Wachabteilung.
          </p>
        </div>
        <div className="order-5 bg-green-50 border border-green-200 rounded-lg p-6">
          <h4 className="text-md font-semibold mb-4 text-green-800">🔊 Sound-Einstellungen</h4>
          
          <div className="flex items-center justify-between p-4 bg-white border border-green-300 rounded">
            <div>
              <div className="font-medium">Bestätigungs-Sound bei Bestellungen</div>
              <div className="text-sm text-gray-600 mt-1">
                Kurzer Bestätigungston wenn eine Bestellung erfolgreich gespeichert wird
              </div>
            </div>
            <button
              onClick={toggleAudio}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                audioEnabled ? 'bg-green-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  audioEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          
          <div className="mt-4 text-sm text-gray-600">
            <p><strong>Info:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Diese Einstellung gilt für alle Benutzer dieses Browsers</li>
              <li>Sound wird nur bei erfolgreich gespeicherten Bestellungen abgespielt</li>
              <li>Beim Aktivieren wird ein Test-Sound abgespielt</li>
            </ul>
          </div>
        </div>

        {/* PayPal Settings */}
        <div className="order-2">
          <h4 className="text-lg font-semibold text-gray-900 mb-1">Bezahlung</h4>
          <p className="text-sm text-gray-500 mb-5">Zahlungswege und externe Zahlungslinks verwalten.</p>
          <PayPalSettings currentDepartment={currentDepartment} />
        </div>

        {/* Abteilungs-Information - moved to bottom */}
        <div className="order-5 bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h4 className="text-md font-semibold mb-4 text-gray-700">ℹ️ Abteilungs-Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <span className="font-medium">Abteilung:</span>
              <span className="ml-2">{currentDepartment.department_name}</span>
            </div>
            <div>
              <span className="font-medium">Abteilungs-ID:</span>
              <span className="ml-2 text-xs text-gray-600">{currentDepartment.department_id}</span>
            </div>
            <div>
              <span className="font-medium">App-Version:</span>
              <span className="ml-2 text-xs text-blue-600 font-semibold">{APP_VERSION}</span>
            </div>
          </div>
          
          {/* Backup Information */}
          <div className="border-t border-gray-300 pt-4">
            <h5 className="text-sm font-semibold mb-2 text-gray-700">🔒 Datenbank & Support</h5>
            <div className="text-sm text-gray-600 space-y-1">
              <div>
                <span className="font-medium">Backup:</span>
                <span className="ml-2">Täglich um 02:00 Uhr, Speicherung 14 Tage</span>
              </div>
              <div>
                <span className="font-medium">Support bei Datenverlust:</span>
                <a href="mailto:kantine@creativey.media" className="ml-2 text-blue-600 hover:text-blue-800 underline">
                  kantine@creativey.media
                </a>
              </div>
            </div>
          </div>
        </div>

      {/* P2: Sponsoring-Einstellungen Card */}
      {sponsoringLoadError && <div role="alert" className="order-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
        <p>{sponsoringLoadError}</p>
        <button type="button" onClick={() => setSponsoringReload(value => value + 1)} className="mt-2 underline">Erneut laden</button>
      </div>}
      {sponsoringConfig && (
        <div className="order-3 bg-white border border-gray-200 rounded-lg p-6" data-testid="sponsoring-config-card">
          <h4 className="text-lg font-semibold text-gray-900 mb-2">Sponsoring</h4>
          <p className="text-sm text-gray-500 mb-4">
            Lege fest, welche Bestandteile beim Sponsoring pro Mahlzeit einbezogen werden und welche Kategorien ausgeschlossen sind.
            Es zählt die Kategorie beim Ausführen. Spätere Änderungen wirken <strong>nicht rückwirkend</strong> — bereits gesponserte Bestellungen bleiben unverändert.
          </p>
          {legacyGuestMeals.length > 0 && <p role="status" className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
            Bisher war „Gäste ausschließen“ aktiv ({legacyGuestMeals.map(meal => ({breakfast: 'Frühstück', lunch: 'Mittagessen', dinner: 'Abendessen'}[meal])).join(', ')}).
            Diese alte Regel gilt für neue Sponsorings nicht mehr. Bitte wähle die gewünschten Kategorien aus und speichere die Einstellungen.
          </p>}

          {/* Frühstück-Bestandteile */}
          <div className="border border-gray-200 rounded-lg p-4 mb-4">
            <h3 className="text-md font-semibold text-gray-800 mb-3">🍳 Frühstück – Was wird gesponsert?</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                ['include_rolls', 'Brötchen (weiß + Körner)'],
                ['include_toppings', 'Beläge (Aufschnitt, Käse …)'],
                ['include_boiled_eggs', eggLabels.boiled],
                ['include_fried_eggs', eggLabels.fried],
                ['include_scrambled_eggs', eggLabels.scrambled],
                ['include_coffee', 'Kaffee'],
              ].map(([key, label]) => (
                <label
                  key={key}
                  className="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-50 cursor-pointer"
                  data-testid={`sponsoring-breakfast-${key}`}
                >
                  <input
                    type="checkbox"
                    checked={!!sponsoringConfig?.breakfast?.[key]}
                    onChange={() => toggleSponsoringOption('breakfast', key)}
                    className="w-4 h-4 text-orange-600 rounded border-gray-300 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Gruppen-Ausschlüsse (je Mahlzeit) */}
          {[
            ['breakfast', '🍳 Frühstück'],
            ['lunch', '🍽️ Mittagessen'],
            ['dinner', '🌙 Abendessen'],
          ].map(([meal, title]) => (
            <div key={meal} className="border border-gray-200 rounded-lg p-4 mb-3">
              <h3 className="text-md font-semibold text-gray-800 mb-3">{title} – Wer wird ausgeschlossen?</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {sponsoringCategories.map(category => <label
                  key={category.id}
                  className="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-50 cursor-pointer"
                  data-testid={`sponsoring-${meal}-category-${category.id}`}
                >
                  <input
                    type="checkbox"
                    disabled={savingSponsoringConfig}
                    checked={(sponsoringConfig?.[meal]?.excluded_category_ids || []).includes(category.id)}
                    onChange={() => toggleSponsoringCategory(meal, category.id)}
                    className="w-4 h-4 text-orange-600 rounded border-gray-300 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">{category.name} ausschließen</span>
                </label>)}
                <label
                  className="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-50 cursor-pointer"
                  data-testid={`sponsoring-${meal}-exclude_external_guests`}
                >
                  <input
                    type="checkbox"
                    disabled={savingSponsoringConfig}
                    checked={!!sponsoringConfig?.[meal]?.exclude_external_guests}
                    onChange={() => toggleSponsoringOption(meal, 'exclude_external_guests')}
                    className="w-4 h-4 text-orange-600 rounded border-gray-300 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">Externe Gäste anderer Wachen ausschließen</span>
                </label>
                {!isSingleDepartment && !stationModeLoading && <label
                  className="flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-50 cursor-pointer"
                  data-testid={`sponsoring-${meal}-exclude_other_departments`}
                >
                  <input
                    type="checkbox"
                    disabled={savingSponsoringConfig}
                    checked={!!sponsoringConfig?.[meal]?.exclude_other_departments}
                    onChange={() => toggleSponsoringOption(meal, 'exclude_other_departments')}
                    className="w-4 h-4 text-orange-600 rounded border-gray-300 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">Mitarbeiter anderer Wachabteilungen ausschließen</span>
                </label>}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                {sponsoringCategories.length === 0 && 'Noch keine Mitarbeiterkategorien angelegt. '}
                Nicht ausgewählte und neue Kategorien sowie Mitarbeiter ohne Kategorie werden berücksichtigt, sofern keine andere Ausschlussregel greift.
              </p>
            </div>
          ))}

          <div className="flex justify-end mt-4">
            <button
              onClick={saveSponsoringConfig}
              disabled={savingSponsoringConfig}
              className="bg-orange-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-orange-700 disabled:bg-gray-400 transition-colors"
              data-testid="save-sponsoring-config-button"
            >
              {savingSponsoringConfig ? 'Speichern …' : 'Sponsoring-Einstellungen speichern'}
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

// PayPal Settings Component
const PayPalSettings = ({ currentDepartment }) => {
  const [paypalSettings, setPaypalSettings] = useState({
    enabled: false,
    breakfast_enabled: false,
    drinks_enabled: false,
    use_separate_links: false,
    combined_link: '',
    breakfast_link: '',
    drinks_link: '',
    append_open_amount: false,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
  
  // Helper function to normalize URLs
  const normalizeUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return '';
    
    // Check if URL already has a protocol
    if (trimmedUrl.match(/^https?:\/\//i)) {
      return trimmedUrl;
    }
    
    // Add https:// if missing
    return `https://${trimmedUrl}`;
  };

  useEffect(() => {
    if (currentDepartment) {
      fetchPayPalSettings();
    }
  }, [currentDepartment]);

  const fetchPayPalSettings = async () => {
    try {
      const response = await axios.get(`${API}/department-paypal-settings/${currentDepartment.department_id}`);
      setPaypalSettings({ append_open_amount: false, ...response.data });
    } catch (error) {
      console.error('Fehler beim Laden der PayPal-Einstellungen:', error);
    }
  };

  const saveSettings = async () => {
    try {
      // Normalize URLs before saving
      const normalizedSettings = {
        ...paypalSettings,
        combined_link: paypalSettings.combined_link ? normalizeUrl(paypalSettings.combined_link) : '',
        breakfast_link: paypalSettings.breakfast_link ? normalizeUrl(paypalSettings.breakfast_link) : '',
        drinks_link: paypalSettings.drinks_link ? normalizeUrl(paypalSettings.drinks_link) : ''
      };
      
      await axios.put(`${API}/department-paypal-settings/${currentDepartment.department_id}`, normalizedSettings);
      setSuccessMessage('✅ PayPal-Einstellungen erfolgreich gespeichert!');
      setShowSuccessNotification(true);
      setIsEditing(false);
      fetchPayPalSettings(); // Refresh data
    } catch (error) {
      console.error('Fehler beim Speichern der PayPal-Einstellungen:', error);
      setSuccessMessage('❌ Fehler beim Speichern: ' + (error.response?.data?.detail || error.message));
      setShowSuccessNotification(true);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    fetchPayPalSettings(); // Reset to original data
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
      <h4 className="text-md font-semibold mb-4 text-blue-800">💳 PayPal-Einstellungen</h4>
      
      {!isEditing ? (
        // Display Mode
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-white border border-blue-300 rounded">
            <div>
              <div className="font-medium">PayPal-Integration</div>
              <div className="text-sm text-gray-600 mt-1">
                Status: <span className={`font-semibold ${paypalSettings.enabled ? 'text-green-600' : 'text-red-600'}`}>
                  {paypalSettings.enabled ? 'Aktiviert' : 'Deaktiviert'}
                </span>
              </div>
              {paypalSettings.enabled && (
                <>
                  <div className="text-sm text-gray-600 mt-1">
                    Buttons: <span className="font-medium">
                      {paypalSettings.breakfast_enabled && paypalSettings.drinks_enabled 
                        ? 'Essen + Getränke' 
                        : paypalSettings.breakfast_enabled 
                          ? 'Nur Essen' 
                          : paypalSettings.drinks_enabled 
                            ? 'Nur Getränke' 
                            : 'Keine aktiviert'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Modus: <span className="font-medium">
                      {paypalSettings.use_separate_links ? 'Getrennte Links' : 'Gemeinsamer Link'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Betrag: <span className="font-medium">
                      {paypalSettings.append_open_amount ? 'Offenen Betrag vorausfüllen' : 'Manuelle Eingabe bei PayPal'}
                    </span>
                  </div>
                </>
              )}
            </div>
            <button
              onClick={() => setIsEditing(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Bearbeiten
            </button>
          </div>
          
          {paypalSettings.enabled && (paypalSettings.breakfast_enabled || paypalSettings.drinks_enabled) && (
            <div className="p-4 bg-white border border-blue-300 rounded">
              <div className="font-medium mb-2">Konfigurierte Links:</div>
              {paypalSettings.use_separate_links ? (
                <div className="space-y-2">
                  {paypalSettings.breakfast_enabled && (
                    <div>
                      <span className="text-sm font-medium text-blue-700">Essen: </span>
                      <span className="text-sm text-gray-600">{paypalSettings.breakfast_link || 'Nicht konfiguriert'}</span>
                    </div>
                  )}
                  {paypalSettings.drinks_enabled && (
                    <div>
                      <span className="text-sm font-medium text-green-700">Getränke: </span>
                      <span className="text-sm text-gray-600">{paypalSettings.drinks_link || 'Nicht konfiguriert'}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <span className="text-sm font-medium text-purple-700">Gemeinsamer Link: </span>
                  <span className="text-sm text-gray-600">{paypalSettings.combined_link || 'Nicht konfiguriert'}</span>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        // Edit Mode
        <div className="space-y-4">
          <div className="p-4 bg-white border border-blue-300 rounded">
            <label className="flex items-center mb-4">
              <input
                type="checkbox"
                checked={paypalSettings.enabled}
                onChange={(e) => setPaypalSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                className="mr-3 h-4 w-4 text-blue-600"
              />
              <span className="font-medium">PayPal-Integration aktivieren</span>
            </label>
            
            {paypalSettings.enabled && (
              <>
                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded">
                  <div className="font-medium mb-2">Welche Buttons sollen aktiviert werden?</div>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={paypalSettings.breakfast_enabled}
                        onChange={(e) => setPaypalSettings(prev => ({ ...prev, breakfast_enabled: e.target.checked }))}
                        className="mr-3 h-4 w-4 text-blue-600"
                      />
                      <span>Essen PayPal-Button</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={paypalSettings.drinks_enabled}
                        onChange={(e) => setPaypalSettings(prev => ({ ...prev, drinks_enabled: e.target.checked }))}
                        className="mr-3 h-4 w-4 text-green-600"
                      />
                      <span>Getränke/Snacks PayPal-Button</span>
                    </label>
                  </div>
                </div>

                {(paypalSettings.breakfast_enabled || paypalSettings.drinks_enabled) && (
                  <label className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      checked={paypalSettings.use_separate_links}
                      onChange={(e) => setPaypalSettings(prev => ({ 
                        ...prev, 
                        use_separate_links: e.target.checked,
                        // Clear opposite links when switching modes
                        combined_link: e.target.checked ? '' : prev.combined_link,
                        breakfast_link: !e.target.checked ? '' : prev.breakfast_link,
                        drinks_link: !e.target.checked ? '' : prev.drinks_link
                      }))}
                      className="mr-3 h-4 w-4 text-blue-600"
                    />
                    <span className="font-medium">Getrennte Links verwenden (sonst gleicher Link für beide)</span>
                  </label>
                )}

                {(paypalSettings.breakfast_enabled || paypalSettings.drinks_enabled) && (
                  <label className="mb-4 flex items-start gap-3 rounded border border-gray-200 bg-gray-50 p-3">
                    <input
                      type="checkbox"
                      checked={!!paypalSettings.append_open_amount}
                      onChange={(e) => setPaypalSettings(prev => ({
                        ...prev,
                        append_open_amount: e.target.checked,
                      }))}
                      className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600"
                    />
                    <span>
                      <span className="block font-medium">Offenen Betrag vorausfüllen</span>
                      <span className="mt-1 block text-xs leading-5 text-gray-500">
                        Hängt den aktuellen negativen Kontosaldo als EUR-Betrag an. Dafür müssen die aktiven Links PayPal.Me-Links sein.
                      </span>
                    </span>
                  </label>
                )}
                
                {(paypalSettings.breakfast_enabled || paypalSettings.drinks_enabled) && (
                  paypalSettings.use_separate_links ? (
                    <div className="space-y-3">
                      {paypalSettings.breakfast_enabled && (
                        <div>
                          <label className="block text-sm font-medium mb-1">Essen PayPal-Link</label>
                          <input
                            type="url"
                            value={paypalSettings.breakfast_link}
                            onChange={(e) => setPaypalSettings(prev => ({ ...prev, breakfast_link: e.target.value }))}
                            placeholder="paypal.me/username (https:// wird automatisch ergänzt)"
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                          />
                          {paypalSettings.breakfast_link && (
                            <div className="text-xs text-gray-500 mt-1">
                              Vorschau: <span className="text-blue-600">{normalizeUrl(paypalSettings.breakfast_link)}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {paypalSettings.drinks_enabled && (
                        <div>
                          <label className="block text-sm font-medium mb-1">Getränke PayPal-Link</label>
                          <input
                            type="url"
                            value={paypalSettings.drinks_link}
                            onChange={(e) => setPaypalSettings(prev => ({ ...prev, drinks_link: e.target.value }))}
                            placeholder="paypal.me/username (https:// wird automatisch ergänzt)"
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                          />
                          {paypalSettings.drinks_link && (
                            <div className="text-xs text-gray-500 mt-1">
                              Vorschau: <span className="text-blue-600">{normalizeUrl(paypalSettings.drinks_link)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Gemeinsamer PayPal-Link 
                        {paypalSettings.breakfast_enabled && paypalSettings.drinks_enabled 
                          ? ' (für Essen + Getränke)'
                          : paypalSettings.breakfast_enabled 
                            ? ' (für Essen)'
                            : ' (für Getränke)'}
                      </label>
                      <input
                        type="url"
                        value={paypalSettings.combined_link}
                        onChange={(e) => setPaypalSettings(prev => ({ ...prev, combined_link: e.target.value }))}
                        placeholder="paypal.me/username (https:// wird automatisch ergänzt)"
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                      />
                      {paypalSettings.combined_link && (
                        <div className="text-xs text-gray-500 mt-1">
                          Vorschau: <span className="text-blue-600">{normalizeUrl(paypalSettings.combined_link)}</span>
                        </div>
                      )}
                    </div>
                  )
                )}
              </>
            )}
          </div>
          
          <div className="flex gap-4">
            <button
              onClick={saveSettings}
              className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
            >
              Einstellungen speichern
            </button>
            <button
              onClick={cancelEdit}
              className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}
      
      <div className="mt-4 text-sm text-gray-600">
        <p><strong>Info:</strong></p>
        <ul className="list-disc list-inside space-y-1">
          <li>PayPal-Links öffnen sich in einem neuen Fenster</li>
          <li>Beispiel: paypal.me/benutzername oder paypal.me/benutzername/betrag</li>
          <li>Links werden nur angezeigt, wenn Mitarbeiter ein negatives Saldo haben</li>
        </ul>
      </div>
      
      {/* Success Notification */}
      {showSuccessNotification && (
        <SuccessNotification
          message={successMessage}
          onClose={() => {
            setShowSuccessNotification(false);
            setSuccessMessage('');
          }}
        />
      )}
    </div>
  );
};

// Coffee and Eggs Management Component - Unified Design with Name Editing
export const CoffeeAndEggsManagement = ({ currentDepartment, breakfastItemVisibility, onUpdateBreakfastItemVisibility }) => {
  const [lunchSettings, setLunchSettings] = useState({ 
    boiled_eggs_price: 0, 
    fried_eggs_price: 0, 
    scrambled_eggs_price: 0,
    coffee_price: 0,
    boiled_eggs_name: '',
    fried_eggs_name: '',
    scrambled_eggs_name: ''
  });
  const [editingItem, setEditingItem] = useState(null); // 'boiled', 'fried', 'scrambled', 'coffee' or null
  const [editPrice, setEditPrice] = useState('');
  const [editName, setEditName] = useState('');
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
  const visibility = {
    boiled_eggs: true,
    fried_eggs: true,
    scrambled_eggs: true,
    coffee: true,
    ...(breakfastItemVisibility || {}),
  };

  const toggleVisibility = (key) => {
    onUpdateBreakfastItemVisibility?.({
      ...visibility,
      [key]: !visibility[key],
    });
  };

  useEffect(() => {
    if (currentDepartment) {
      fetchLunchSettings();
    }
  }, [currentDepartment]);

  const fetchLunchSettings = async () => {
    try {
      // Get department-specific settings
      const response = await axios.get(`${API}/department-settings/${currentDepartment.department_id}`);
      
      setLunchSettings({
        boiled_eggs_price: response.data.boiled_eggs_price || 0.50,
        fried_eggs_price: response.data.fried_eggs_price || 0.50,
        scrambled_eggs_price: response.data.scrambled_eggs_price || 0.50,
        coffee_price: response.data.coffee_price || 1.50,
        boiled_eggs_name: response.data.boiled_eggs_name || '',
        fried_eggs_name: response.data.fried_eggs_name || '',
        scrambled_eggs_name: response.data.scrambled_eggs_name || ''
      });
    } catch (error) {
      console.error('Fehler beim Laden der Department-Einstellungen:', error);
      // Fallback to default settings
      setLunchSettings({
        boiled_eggs_price: 0.50,
        fried_eggs_price: 0.50,
        scrambled_eggs_price: 0.50,
        coffee_price: 1.50,
        boiled_eggs_name: '',
        fried_eggs_name: '',
        scrambled_eggs_name: ''
      });
    }
  };

  const startEdit = (item, currentPrice, currentName) => {
    setEditingItem(item);
    setEditPrice(currentPrice.toFixed(2));
    setEditName(currentName || '');
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setEditPrice('');
    setEditName('');
  };

  const updateItem = async () => {
    if (!editPrice || isNaN(parseFloat(editPrice))) {
      setSuccessMessage('Bitte gültigen Preis eingeben');
      setShowSuccessNotification(true);
      return;
    }

    try {
      const price = parseFloat(editPrice);
      let priceEndpoint;
      let successMsg;
      let eggType;
      
      if (editingItem === 'boiled') {
        priceEndpoint = `${API}/department-settings/${currentDepartment.department_id}/boiled-eggs-price`;
        successMsg = 'Gekochte Eier erfolgreich aktualisiert';
        eggType = 'boiled';
      } else if (editingItem === 'fried') {
        priceEndpoint = `${API}/department-settings/${currentDepartment.department_id}/fried-eggs-price`;
        successMsg = 'Spiegeleier erfolgreich aktualisiert';
        eggType = 'fried';
      } else if (editingItem === 'scrambled') {
        priceEndpoint = `${API}/department-settings/${currentDepartment.department_id}/scrambled-eggs-price`;
        successMsg = 'Rührei erfolgreich aktualisiert';
        eggType = 'scrambled';
      } else {
        priceEndpoint = `${API}/department-settings/${currentDepartment.department_id}/coffee-price`;
        successMsg = 'Kaffee-Preis erfolgreich aktualisiert';
      }
      
      // Update price
      await axios.put(priceEndpoint, null, { params: { price } });
      
      // Update name if it's an egg type and name was provided
      if (eggType && editName.trim()) {
        await axios.put(
          `${API}/department-settings/${currentDepartment.department_id}/egg-name`,
          null,
          { params: { egg_type: eggType, name: editName.trim() } }
        );
      }
      
      await fetchLunchSettings();
      setEditingItem(null);
      setEditPrice('');
      setEditName('');
      
      setSuccessMessage(successMsg);
      setShowSuccessNotification(true);
    } catch (error) {
      console.error('Fehler beim Aktualisieren:', error);
      setSuccessMessage('Fehler beim Aktualisieren');
      setShowSuccessNotification(true);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <h4 className="mb-4 text-base font-semibold text-gray-900">Eierspeisen & Kaffee</h4>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Gekochte Eier */}
        <div className={`relative rounded-lg border p-4 transition-all ${visibility.boiled_eggs ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-60'}`}>
          <button
            type="button"
            onClick={() => toggleVisibility('boiled_eggs')}
            className="absolute top-2 right-2 rounded-full p-1.5 transition-colors hover:bg-gray-200"
            title={visibility.boiled_eggs ? 'Deaktivieren' : 'Aktivieren'}
            data-testid="breakfast-visibility-boiled-eggs"
          >
            {visibility.boiled_eggs ? <Eye className="h-4 w-4 text-slate-700" /> : <EyeOff className="h-4 w-4 text-gray-400" />}
          </button>
          {editingItem === 'boiled' ? (
            <div className="space-y-3">
              <div className="font-medium text-gray-700">Gekochte Eier bearbeiten</div>
              <div>
                <label className="block text-sm font-medium mb-1">Name (optional)</label>
                <input
                  type="text"
                  placeholder="Gekochte Frühstückseier"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
                <p className="text-xs text-gray-500 mt-1">Leer lassen für Standard-Namen</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Stückpreis (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editPrice}
                  onChange={(e) => setEditPrice(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={updateItem}
                  className="rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700"
                >
                  Speichern
                </button>
                <button
                  onClick={cancelEdit}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="font-medium">{lunchSettings.boiled_eggs_name || 'Gekochte Frühstückseier'}</div>
              <div className="text-lg font-semibold text-gray-900">{lunchSettings.boiled_eggs_price.toFixed(2)} € <span className="text-xs font-normal text-gray-500">pro Ei</span></div>
              <button
                onClick={() => startEdit('boiled', lunchSettings.boiled_eggs_price, lunchSettings.boiled_eggs_name)}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Bearbeiten
              </button>
            </div>
          )}
        </div>

        {/* Spiegeleier */}
        <div className={`relative rounded-lg border p-4 transition-all ${visibility.fried_eggs ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-60'}`}>
          <button
            type="button"
            onClick={() => toggleVisibility('fried_eggs')}
            className="absolute top-2 right-2 rounded-full p-1.5 transition-colors hover:bg-gray-200"
            title={visibility.fried_eggs ? 'Deaktivieren' : 'Aktivieren'}
            data-testid="breakfast-visibility-fried-eggs"
          >
            {visibility.fried_eggs ? <Eye className="h-4 w-4 text-slate-700" /> : <EyeOff className="h-4 w-4 text-gray-400" />}
          </button>
          {editingItem === 'fried' ? (
            <div className="space-y-3">
              <div className="font-medium text-gray-700">Spiegeleier bearbeiten</div>
              <div>
                <label className="block text-sm font-medium mb-1">Name (optional)</label>
                <input
                  type="text"
                  placeholder="Spiegeleier"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
                <p className="text-xs text-gray-500 mt-1">Leer lassen für Standard-Namen</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Stückpreis (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editPrice}
                  onChange={(e) => setEditPrice(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={updateItem}
                  className="rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700"
                >
                  Speichern
                </button>
                <button
                  onClick={cancelEdit}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="font-medium">{lunchSettings.fried_eggs_name || 'Spiegeleier'}</div>
              <div className="text-lg font-semibold text-gray-900">{lunchSettings.fried_eggs_price.toFixed(2)} € <span className="text-xs font-normal text-gray-500">pro Ei</span></div>
              <button
                onClick={() => startEdit('fried', lunchSettings.fried_eggs_price, lunchSettings.fried_eggs_name)}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Bearbeiten
              </button>
            </div>
          )}
        </div>

        {/* Rührei - NEU */}
        <div className={`relative rounded-lg border p-4 transition-all ${visibility.scrambled_eggs ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-60'}`}>
          <button
            type="button"
            onClick={() => toggleVisibility('scrambled_eggs')}
            className="absolute top-2 right-2 rounded-full p-1.5 transition-colors hover:bg-gray-200"
            title={visibility.scrambled_eggs ? 'Deaktivieren' : 'Aktivieren'}
            data-testid="breakfast-visibility-scrambled-eggs"
          >
            {visibility.scrambled_eggs ? <Eye className="h-4 w-4 text-slate-700" /> : <EyeOff className="h-4 w-4 text-gray-400" />}
          </button>
          {editingItem === 'scrambled' ? (
            <div className="space-y-3">
              <div className="font-medium text-gray-700">Rührei bearbeiten</div>
              <div>
                <label className="block text-sm font-medium mb-1">Name (optional)</label>
                <input
                  type="text"
                  placeholder="Rührei"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
                <p className="text-xs text-gray-500 mt-1">Leer lassen für Standard-Namen</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Stückpreis (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editPrice}
                  onChange={(e) => setEditPrice(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={updateItem}
                  className="rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700"
                >
                  Speichern
                </button>
                <button
                  onClick={cancelEdit}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="font-medium">{lunchSettings.scrambled_eggs_name || 'Rührei'}</div>
              <div className="text-lg font-semibold text-gray-900">{lunchSettings.scrambled_eggs_price.toFixed(2)} € <span className="text-xs font-normal text-gray-500">pro Ei</span></div>
              <button
                onClick={() => startEdit('scrambled', lunchSettings.scrambled_eggs_price, lunchSettings.scrambled_eggs_name)}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Bearbeiten
              </button>
            </div>
          )}
        </div>

        {/* Kaffee */}
        <div className={`relative rounded-lg border p-4 transition-all ${visibility.coffee ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-60'}`}>
          <button
            type="button"
            onClick={() => toggleVisibility('coffee')}
            className="absolute top-2 right-2 rounded-full p-1.5 transition-colors hover:bg-gray-200"
            title={visibility.coffee ? 'Deaktivieren' : 'Aktivieren'}
            data-testid="breakfast-visibility-coffee"
          >
            {visibility.coffee ? <Eye className="h-4 w-4 text-slate-700" /> : <EyeOff className="h-4 w-4 text-gray-400" />}
          </button>
          {editingItem === 'coffee' ? (
            <div className="space-y-3">
              <div className="font-medium text-gray-700">Kaffee bearbeiten</div>
              <div>
                <label className="block text-sm font-medium mb-1">Preis pro Tag (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editPrice}
                  onChange={(e) => setEditPrice(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={updateItem}
                  className="rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700"
                >
                  Speichern
                </button>
                <button
                  onClick={cancelEdit}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="font-medium">Kaffee</div>
              <div className="text-lg font-semibold text-gray-900">{lunchSettings.coffee_price.toFixed(2)} € <span className="text-xs font-normal text-gray-500">pro Tag</span></div>
              <button
                onClick={() => startEdit('coffee', lunchSettings.coffee_price, '')}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Bearbeiten
              </button>
            </div>
          )}
          <CoffeeFlatPricing departmentId={currentDepartment.department_id} apiBase={API} />
        </div>
      </div>
      
      {/* Success Notification */}
      {showSuccessNotification && (
        <SuccessNotification
          message={successMessage}
          onClose={() => {
            setShowSuccessNotification(false);
            setSuccessMessage('');
          }}
        />
      )}
    </div>
  );
};

// ERWEITERT: Flexible Payment Modal Component (inkl. Subkonto-Support)
export const FlexiblePaymentModal = ({ employee, paymentType, accountLabel, onClose, onPayment, isSubaccount = false, currentDepartment = null, needsAccountTypeSelection = false }) => {
  const networkIdentity = useRef(createRetryIdentity());
  const [submitting, setSubmitting] = useState(false);
  const paymentBusy = useRef(false);
  const [amount, setAmount] = useState('');
  const [direction, setDirection] = useState(PAYMENT_DIRECTION_DEPOSIT);
  const [notes, setNotes] = useState('');
  const [bookedBy, setBookedBy] = useState('');
  
  // CRITICAL FIX: Use department config to determine combined accounts, not field existence
  const hasCombinedAccount = employee.use_combined_accounts === true;
  const [selectedAccountType, setSelectedAccountType] = useState(
    paymentType || 
    (hasCombinedAccount ? 'combined' : (needsAccountTypeSelection ? '' : 'breakfast'))
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (paymentBusy.current) return;
    const parsedAmount = signedPaymentAmount(amount, direction);
    
    if (amount && !isNaN(parsedAmount)) {
      // Check if booked_by is filled
      if (!bookedBy.trim()) {
        alert('Bitte geben Sie den Namen der buchenden Person ein.');
        return;
      }
      
      // Check if account type is selected for subaccounts (NUR bei getrennten Konten)
      if (needsAccountTypeSelection && !selectedAccountType && !hasCombinedAccount) {
        alert('Bitte wählen Sie einen Kontotyp aus.');
        return;
      }
      
      // Check if this is a withdrawal and notes are required
      if (direction === PAYMENT_DIRECTION_WITHDRAWAL && !notes.trim()) {
        alert('Bei Auszahlungen ist eine Notiz erforderlich.');
        return;
      }
      
      const currentPaymentType = selectedAccountType || paymentType;
      
      paymentBusy.current = true; setSubmitting(true);
      let result;
      if (isSubaccount) {
        // Subaccount payment - includes department info
        // KORRIGIERT: Map 'drinks' to 'drinks_sweets' for backend compatibility
        const backendPaymentType = currentPaymentType === 'drinks' ? 'drinks_sweets' : currentPaymentType;
        result = await onPayment({
          network_request_id: employee.is_external_guest ? networkIdentity.current.forPayload({employee_id:employee.id, amount:parsedAmount, type:currentPaymentType, notes:notes.trim(), booked_by:bookedBy.trim()}) : undefined,
          employee_id: employee.id,
          balance_type: currentPaymentType, // Keep original for subaccount logic (breakfast/drinks)
          payment_type: backendPaymentType, // Use backend-compatible format
          amount: parsedAmount,
          payment_method: 'cash', // Default method
          notes: notes.trim(),
          booked_by: bookedBy.trim(),
          admin_department: currentDepartment?.department_id,
          isSubaccount: true  // KORRIGIERT: Explicit flag hinzufügen
        });
      } else {
        // Normal payment - FIXED: Include admin_department for combined accounts support
        result = await onPayment({
          network_request_id: employee.is_external_guest ? networkIdentity.current.forPayload({employee_id:employee.id, amount:parsedAmount, type:currentPaymentType, notes:notes.trim(), booked_by:bookedBy.trim()}) : undefined,
          employee_id: employee.id,
          payment_type: currentPaymentType,
          amount: parsedAmount,
          notes: notes.trim(),
          booked_by: bookedBy.trim(),
          admin_department: currentDepartment?.department_id || employee.department_id  // Include department for backend validation
        });
      }
      paymentBusy.current = false; setSubmitting(false);
      if (result === false) return;
      onClose();
    }
  };

  const modalTitle = isSubaccount 
    ? `Subkonto-Verwaltung für ${employee.name}`
    : `Ein-/Auszahlung für ${employee.name}`;
    
  const accountInfo = isSubaccount 
    ? `${accountLabel} in ${currentDepartment?.department_name || 'Andere Abteilung'}`
    : accountLabel;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 py-8 overflow-y-auto"
      onClick={(e) => {
        // Close modal when clicking on overlay (outside the modal content)
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
      >
        <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">{modalTitle}</h2>
        <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">Konto: {accountInfo}</p>
        
        {/* Current Balance Display for Subaccounts */}
        {isSubaccount && employee.subaccount_balance && (
          <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-gray-50 rounded">
            <h3 className="text-xs sm:text-sm font-medium mb-2">Aktueller Subkonto-Saldo:</h3>
            <div className="text-xs sm:text-sm grid grid-cols-2 gap-2">
              {employee.use_combined_accounts ? (
                /* COMBINED ACCOUNTS: Zeige nur Gesamtsaldo */
                <div className="col-span-2">
                  <span className="text-gray-600">Gesamtsaldo:</span>
                  <span className={`ml-1 font-semibold ${employee.subaccount_balance.combined >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {employee.subaccount_balance.combined.toFixed(2)}€
                  </span>
                </div>
              ) : (
                /* SEPARATE ACCOUNTS: Zeige getrennte Konten */
                <>
                  <div>
                    <span className="text-gray-600">Essen:</span>
                    <span className={`ml-1 font-semibold ${(employee.subaccount_balance.breakfast || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {(employee.subaccount_balance.breakfast || 0).toFixed(2)}€
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Getränke:</span>
                    <span className={`ml-1 font-semibold ${(employee.subaccount_balance.drinks || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {(employee.subaccount_balance.drinks || 0).toFixed(2)}€
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          {/* ERWEITERT: Account Type Selection für Subkonten - NUR bei getrennten Konten */}
          {needsAccountTypeSelection && isSubaccount && !employee.use_combined_accounts && (
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-1 sm:mb-2">
                Kontotyp auswählen
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedAccountType('breakfast')}
                  className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                    selectedAccountType === 'breakfast'
                      ? 'bg-blue-100 border-blue-500 text-blue-700'
                      : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Essen
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedAccountType('drinks')}
                  className={`px-3 py-2 rounded-lg text-sm border transition-colors ${
                    selectedAccountType === 'drinks'
                      ? 'bg-blue-100 border-blue-500 text-blue-700'
                      : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Getränke/Snacks
                </button>
              </div>
              {selectedAccountType && (
                <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                  {selectedAccountType === 'breakfast' && 
                    `Aktueller Saldo: ${employee.subaccount_balance?.breakfast?.toFixed(2) || '0.00'}€`
                  }
                  {selectedAccountType === 'drinks' && 
                    `Aktueller Saldo: ${employee.subaccount_balance?.drinks?.toFixed(2) || '0.00'}€`
                  }
                </div>
              )}
            </div>
          )}
          
          <PaymentDirectionSelector value={direction} onChange={setDirection} />

          <div>
            <label className="block text-xs sm:text-sm font-medium mb-1 sm:mb-2">
              Betrag (€)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/-/g, ''))}
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm sm:text-base"
              required
              autoFocus
              placeholder="10.00"
              data-testid="payment-amount"
            />
          </div>
          
          <div>
            <label className="block text-xs sm:text-sm font-medium mb-1 sm:mb-2">
              Notizen {direction === PAYMENT_DIRECTION_WITHDRAWAL ? '(erforderlich bei Auszahlungen)' : '(optional)'}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm sm:text-base"
              rows="2"
              placeholder={direction === PAYMENT_DIRECTION_WITHDRAWAL ? "Grund für Auszahlung angeben..." : "Zusätzliche Informationen..."}
              required={direction === PAYMENT_DIRECTION_WITHDRAWAL}
            />
          </div>
          
          <div>
            <label className="block text-xs sm:text-sm font-medium mb-1 sm:mb-2">
              Gebucht von <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={bookedBy}
              onChange={(e) => setBookedBy(e.target.value)}
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm sm:text-base"
              required
              placeholder="Ihr Name"
              data-testid="payment-booked-by"
            />
          </div>
          
          <div className="flex gap-2 sm:gap-4 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-500 text-white py-2 px-3 sm:px-4 rounded-lg hover:bg-gray-600 text-sm sm:text-base"
            >
              Abbrechen
            </button>
            <button
              type="submit" disabled={submitting}
              className="flex-1 bg-green-600 text-white py-2 px-3 sm:px-4 rounded-lg hover:bg-green-700 text-sm sm:text-base"
            >
              {isSubaccount ? 'Subkonto-Buchung verbuchen' : 'Ein-/Auszahlung verbuchen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
